import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users, Search, Filter, FileDown, FileText,
  Edit2, Trash2, Eye, GraduationCap, School, Calendar,
  Phone, MapPin, AlertCircle, ChevronLeft, ChevronRight,
  X, CheckCircle2, Loader2, ArrowUpDown, ArrowUp, ArrowDown,
  UserPlus, FileSpreadsheet, HeartPulse,
  User, Upload, Wrench, PenLine, Check, Copy
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { studentsApi, schoolApi, classesApi, cyclesApi, levelsApi, apiService } from '@/services/api'
import { useAuthStore } from '@/store/authStore'

const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'
const FULL_ACCESS_ROLES = ['super_admin', 'admin', 'proviseur', 'censeur']

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getUserSchoolId error:', e) }
  return null
}

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yearId) return Number(yearId)
  } catch (e) { console.warn('getSelectedYearId error:', e) }
  return null
}

const getSelectedYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) } catch (e) { return null }
}

interface Student {
  id: number; matricule: string; first_name: string; last_name: string; sex: string
  date_of_birth?: string; place_of_birth?: string; nationality?: string
  class_name?: string; class_code?: string; class_abbreviation?: string
  level_name?: string; level_code?: string; section_name?: string; section_code?: string
  cycle_name?: string; cycle_code?: string; school_name?: string; status: string
  handicap?: boolean; social_case?: boolean; emergency_phone?: string
  enrollment_type?: string; created_at?: string; class_id?: number; level_id?: number
  section_id?: number; cycle_id?: number; enrollment_id?: number
  enrollment_status?: string; enrollment_date?: string; email?: string; phone?: string
  address?: string; father_name?: string; mother_name?: string; emergency_contact?: string
  blood_group?: string; allergies?: string; previous_school?: string; photo_url?: string; is_repeating?: boolean; full_name?: string
  section_label_fr?: string; section_label_en?: string
  level_label_fr?: string; level_label_en?: string
  cycle_label_fr?: string; cycle_label_en?: string
  school_id?: number; academic_year_id?: number
}

interface Section { id: number; code: string; label_fr: string; label_en: string; school_id?: number }
interface Cycle { id: number; section_id: number; code: string; label_fr: string; label_en: string; order: number }
interface Level { id: number; cycle_id: number; code: string; label_fr: string; label_en: string; order: number }
interface ClassItem { id: number; level_id: number; specialty_id?: number; code: string; abbreviation: string; name: string; capacity?: number; status?: string }

interface FilterState {
  search: string; section_id: string; cycle_id: string; level_id: string; class_id: string
  status: string; sex: string; handicap: string; social_case: string
}

function cn(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('fr-FR')
}

function resolvePhotoUrl(url?: string | null): string | null {
  if (!url) return null
  if (url.startsWith('http')) return url
  if (url.startsWith('/')) return `http://127.0.0.1:8001${url}`
  return `http://127.0.0.1:8001/${url}`
}

function getStatusBadge(status?: string) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Actif' },
    NOUVEAU: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Nouveau' },
    INSCRIT: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Inscrit' },
    EXCLU: { bg: 'bg-red-100', text: 'text-red-700', label: 'Exclu' },
    DEMISSION: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Démission' },
    RENVOYE: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Renvoyé' },
    REINTEGRE: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Réintégré' },
    TRANSFERE: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Transféré' },
    INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Inactif' },
    ANCIEN: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Ancien' },
    diplome: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Diplômé' },
  }
  const s = map[status || ''] || map[status?.toLowerCase() || ''] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status || 'N/A' }
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', s.bg, s.text)}>
      {s.label}
    </span>
  )
}

// Parseur commun des réponses API élèves (partagé entre loadStudents et le scan de réparation)
function parseStudentsResponse(res: any): { items: Student[]; totalCount: number } {
  let items: Student[] = []
  let totalCount = 0
  if (res && typeof res === 'object') {
    if (Array.isArray(res.items)) { items = res.items; totalCount = res.total ?? items.length }
    else if (Array.isArray(res.data)) { items = res.data; totalCount = res.total ?? items.length }
    else if (Array.isArray(res.results)) { items = res.results; totalCount = res.total ?? items.length }
    else if (Array.isArray(res)) { items = res; totalCount = items.length }
  }
  return { items, totalCount }
}

// Détecte les noms dupliqués : le nom complet a été enregistré dans last_name ET first_name
function isDuplicatedName(s: Student): boolean {
  return !!s.last_name && !!s.first_name &&
    s.last_name.trim().toLowerCase() === s.first_name.trim().toLowerCase()
}

function exportToCSV(students: Student[], filename: string) {
  const headers = ['N°', 'Matricule', 'Nom', 'Prénom', 'Sexe', 'Date Naissance', 'Lieu Naissance', 'Nationalité', 'Classe', 'Niveau', 'Section', 'Téléphone Urgence', 'Contact Tuteur', 'Statut', 'Handicap', 'Cas Social', 'Redoublant', 'Date Inscription']
  const rows = students.map((s, i) => [String(i + 1), s.matricule, s.last_name, s.first_name, s.sex === 'M' ? 'Masculin' : 'Féminin', formatDate(s.date_of_birth), s.place_of_birth || '-', s.nationality || '-', s.class_name || '-', s.level_name || '-', s.section_name || '-', s.emergency_phone || '-', s.emergency_contact || '-', s.status, s.handicap ? 'Oui' : 'Non', s.social_case ? 'Oui' : 'Non', s.is_repeating ? 'Oui' : 'Non', formatDate(s.created_at)])
  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n')
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${filename}.csv`
  link.click()
  URL.revokeObjectURL(link.href)
}

function exportToExcel(students: Student[], filename: string) {
  const headers = ['N°', 'Matricule', 'Nom', 'Prénom', 'Sexe', 'Date Naissance', 'Lieu Naissance', 'Nationalité', 'Classe', 'Niveau', 'Section', 'Téléphone Urgence', 'Contact Tuteur', 'Statut', 'Handicap', 'Cas Social', 'Redoublant', 'Date Inscription']
  const rows = students.map((s, i) => [String(i + 1), s.matricule, s.last_name, s.first_name, s.sex === 'M' ? 'Masculin' : 'Féminin', formatDate(s.date_of_birth), s.place_of_birth || '-', s.nationality || '-', s.class_name || '-', s.level_name || '-', s.section_name || '-', s.emergency_phone || '-', s.emergency_contact || '-', s.status, s.handicap ? 'Oui' : 'Non', s.social_case ? 'Oui' : 'Non', s.is_repeating ? 'Oui' : 'Non', formatDate(s.created_at)])
  let html = '<table><thead><tr>'
  headers.forEach(h => html += `<th>${h}</th>`)
  html += '</tr></thead><tbody>'
  rows.forEach(row => { html += '<tr>' + row.map(cell => `<td>${cell}</td>`).join('') + '</tr>' })
  html += '</tbody></table>'
  const blob = new Blob([html], { type: 'application/vnd.ms-excel' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${filename}.xls`
  link.click()
  URL.revokeObjectURL(link.href)
}

function exportToPDF(students: Student[], filename: string, subtitle?: string) {
  const headers = ['N°', 'Matricule', 'Nom', 'Prénom', 'Sexe', 'Date Naiss.', 'Lieu Naiss.', 'Nationalité', 'Classe', 'Tél. Urgence', 'Contact Tuteur', 'Redoublant', 'Statut']
  const rows = students.map((s, i) => [String(i + 1), s.matricule, s.last_name, s.first_name, s.sex === 'M' ? 'M' : 'F', formatDate(s.date_of_birth), s.place_of_birth || '-', s.nationality || '-', s.class_name || '-', s.emergency_phone || '-', s.emergency_contact || '-', s.is_repeating ? 'Oui' : 'Non', s.status])
  const printWindow = window.open('', '_blank')
  if (!printWindow) return
  // .footer en position:fixed → répété en bas de CHAQUE page imprimée
  // thead en table-header-group → ligne d'en-tête répétée sur chaque page
  printWindow.document.write(`<html><head><title>${filename}</title><style>*{-webkit-print-color-adjust:exact;print-color-adjust:exact}body{font-family:Arial,sans-serif;margin:20px 20px 45px 20px}h1{text-align:center;color:#333;font-size:18px;margin-bottom:4px}.subtitle{text-align:center;color:#666;font-size:12px;margin-bottom:20px}table{width:100%;border-collapse:collapse;font-size:10px;border:2px solid #333}thead{display:table-header-group}tr{page-break-inside:avoid}th,td{border:1.5px solid #444;padding:6px;text-align:left}th{background-color:#059669;color:white;font-weight:bold;font-size:10.5px;border:1.5px solid #333}tr:nth-child(even){background-color:#f2f2f2}.summary{text-align:center;margin-top:12px;font-size:10px;color:#666}.footer{position:fixed;bottom:0;left:0;right:0;text-align:center;font-size:10px;color:#666;padding:6px 0;border-top:1px solid #ddd;background:white}</style></head><body><h1>Liste des Élèves - Lycée Bilingue de Baleng</h1>${subtitle ? `<div class="subtitle">${subtitle}</div>` : ''}<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map(row => `<tr>${row.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table><div class="summary">Généré le ${new Date().toLocaleDateString('fr-FR')} - Total: ${students.length} élèves</div><div class="footer">myhelperschool.cm</div></body></html>`)
  printWindow.document.close()
  printWindow.print()
}

function detectUserRole(auth: any): { role: string; canModify: boolean; isReadonly: boolean } {
  const isAdminFlag = auth?.isAdmin === true || auth?.isSuperAdmin === true
  const roleName = (auth?.user?.roleName as string) || ''
  const roleNameLower = roleName.toLowerCase().trim()
  const role = (auth?.user?.role as string) || ''
  const roleLower = role.toLowerCase().trim()
  const perms = auth?.permissions || []
  const hasManagePermission = Array.isArray(perms) && (perms.includes('students.manage') || perms.includes('students.create') || perms.includes('students.delete') || perms.includes('students.update') || perms.includes('admin') || perms.includes('full_access'))
  let localRole = ''
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      localRole = (state?.user?.roleName as string) || (state?.user?.role as string) || ''
    }
  } catch (e) { }
  const localRoleLower = localRole.toLowerCase().trim()
  let finalRole = roleNameLower || roleLower || localRoleLower || 'unknown'
  if (isAdminFlag) finalRole = 'admin'
  const canModify = isAdminFlag || hasManagePermission || FULL_ACCESS_ROLES.includes(finalRole) || FULL_ACCESS_ROLES.includes(roleNameLower) || FULL_ACCESS_ROLES.includes(roleLower) || FULL_ACCESS_ROLES.includes(localRoleLower)
  return { role: finalRole, canModify, isReadonly: !canModify }
}

export function StudentsPage() {
  const { lang } = useLang()
  const auth = useAuthStore()
  const navigate = useNavigate()

  const { canModify, isReadonly } = useMemo(() => detectUserRole(auth), [auth])

  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId)
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId)
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null)

  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage] = useState(20)
  const [showFilters, setShowFilters] = useState(false)
  // V10 : tri SERVEUR (toutes les pages, pas seulement la page courante).
  // Défaut : nom de famille A→Z — le premier élève affiché commence par A.
  const [sortField, setSortField] = useState<string>('last_name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)
  const [editForm, setEditForm] = useState<Partial<Student>>({})
  const [editLoading, setEditLoading] = useState(false)
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null)
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null)
  const [editPhotoUploading, setEditPhotoUploading] = useState(false)

  const [sections, setSections] = useState<Section[]>([])
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [levels, setLevels] = useState<Level[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [refLoading, setRefLoading] = useState(false)

  const [filters, setFilters] = useState<FilterState>({
    search: '', section_id: '', cycle_id: '', level_id: '', class_id: '',
    status: '', sex: '', handicap: '', social_case: '',
  })

  const [deactivateModal, setDeactivateModal] = useState<{open: boolean; studentId: number | null}>({ open: false, studentId: null })

  // ─── Bulk contacts update states ───
  const [contactsModalOpen, setContactsModalOpen] = useState(false)
  const [contactsFile, setContactsFile] = useState<File | null>(null)
  const [contactsLoading, setContactsLoading] = useState(false)
  const [contactsClassId, setContactsClassId] = useState<string>('')

  // ─── Export : indique qu'un export complet est en cours ───
  const [exporting, setExporting] = useState(false)

  // ─── ÉDITION INLINE DU NOM (2e bouton Modifier dans le tableau) ───
  const [inlineEditId, setInlineEditId] = useState<number | null>(null)
  const [inlineMatricule, setInlineMatricule] = useState('')
  const [inlineLastName, setInlineLastName] = useState('')
  const [inlineFirstName, setInlineFirstName] = useState('')
  const [inlineSaving, setInlineSaving] = useState(false)

  // ─── RÉPARATION EN MASSE DES NOMS DUPLIQUÉS ───
  const [repairModalOpen, setRepairModalOpen] = useState(false)
  const [repairList, setRepairList] = useState<{ student: Student; name: string; checked: boolean; repaired?: boolean }[]>([])
  const [repairScanning, setRepairScanning] = useState(false)
  const [repairRunning, setRepairRunning] = useState(false)
  const [repairScanned, setRepairScanned] = useState(false)
  const [repairProgress, setRepairProgress] = useState<{ done: number; total: number; failed: number }>({ done: 0, total: 0, failed: 0 })

  // ─── GESTION DES DOUBLONS (même nom + même date de naissance) ───
  const [dupModalOpen, setDupModalOpen] = useState(false)
  const [dupGroups, setDupGroups] = useState<any[]>([])
  const [dupScanning, setDupScanning] = useState(false)
  const [dupScanned, setDupScanned] = useState(false)
  const [dupRunning, setDupRunning] = useState(false)
  const [dupProgress, setDupProgress] = useState<{ done: number; total: number; failed: number }>({ done: 0, total: 0, failed: 0 })
  // V11 : modal de confirmation de suppression (remplace window.confirm natif)
  const [dupConfirmModal, setDupConfirmModal] = useState<{ open: boolean; targets: Student[] }>({ open: false, targets: [] })

  // V12 : totaux RÉELS (endpoint /students/stats/cards, par année scolaire)
  const [statsCards, setStatsCards] = useState<{ total: number; active: number; handicaps: number; social_cases: number } | null>(null)

  // V15 : détection des doublons — portée ('school' = toute l'école toutes années,
  // 'displayed' = liste affichée avec les filtres actifs dont la classe)
  // et critère ('strict' = nom + date naissance, 'name' = nom seul)
  const [dupScope, setDupScope] = useState<'school' | 'displayed'>('school')
  const [dupMode, setDupMode] = useState<'strict' | 'name'>('name')

  const $t = useMemo(() => ({
    fr: {
      title: 'Gestion des élèves', subtitle: 'élèves enregistrés',
      newStudent: 'Nouvel élève', search: 'Rechercher...',
      advancedFilters: 'Filtres avancés', resetFilters: 'Réinitialiser les filtres',
      allSections: 'Toutes les sections', allCycles: 'Tous les cycles',
      allLevels: 'Tous les niveaux', allClasses: 'Toutes les classes',
      section: 'Section', cycle: 'Cycle', level: 'Niveau', class_: 'Classe',
      status: 'Statut', sex: 'Sexe', handicap: 'Handicap', socialCase: 'Cas social',
      totalStudents: 'Total élèves', active: 'Actifs',
      noData: 'Aucun élève trouvé', loading: 'Chargement...',
      noYear: 'Aucune année scolaire sélectionnée',
      noYearMsg: 'Veuillez sélectionner une année scolaire dans le header',
      readonlyMsg: 'Lecture seule — modifications désactivées',
      regNo: 'Matricule', name: 'Nom & Prénom', birth: 'Naissance',
      contact: 'Contact', actions: 'Actions', view: 'Voir', edit: 'Modifier', delete: 'Désactiver (INACTIF)',
      exportCSV: 'Export CSV', exportExcel: 'Export Excel', exportPDF: 'Export PDF',
      yearChanged: 'Année scolaire changée — rechargement des données',
      contextUpdated: 'Contexte scolaire mis à jour — rechargement',
      errorLoading: 'Erreur chargement élèves',
      deleteConfirm: 'Désactiver cet élève ? Cette action changera son statut à INACTIF. Continuer ?',
      deleteSuccess: 'Élève désactivé',
      deleteError: 'Erreur désactivation',
      filterByStructure: 'Filtrer par structure académique',
      activeFilters: 'Filtres actifs:',
      readonlyTooltip: 'Seuls super_admin, admin, proviseur et censeur peuvent modifier',
      studentDetails: "Détails de l'élève",
      editStudent: "Modifier l'élève",
      close: 'Fermer',
      save: 'Enregistrer',
      personalInfo: 'Informations personnelles',
      academicInfo: 'Informations académiques',
      emergencySection: "Contact d'urgence",
      firstName: 'Prénom',
      lastName: 'Nom',
      dateOfBirth: 'Date de naissance',
      placeOfBirth: 'Lieu de naissance',
      nationality: 'Nationalité',
      sexe: 'Sexe',
      phone: 'Téléphone',
      email: 'Email',
      address: 'Adresse',
      fatherName: 'Nom du père',
      motherName: 'Nom de la mère',
      bloodGroup: 'Groupe sanguin',
      allergies: 'Allergies',
      previousSchool: 'École précédente',
      enrollmentDate: "Date d'inscription",
      enrollmentType: "Type d'inscription",
      className: 'Classe',
      levelName: 'Niveau',
      sectionName: 'Section',
      matricule: 'Matricule',
      statusLabel: 'Statut',
      handicapLabel: 'Handicap',
      socialCaseLabel: 'Cas social',
      yes: 'Oui',
      no: 'Non',
      male: 'Masculin',
      female: 'Féminin',
      cancel: 'Annuler',
      updateSuccess: 'Élève mis à jour',
      updateError: 'Erreur mise à jour',
      notAssigned: 'Non assignée',
      notAssignedLevel: 'Non assigné',
      notAssignedSection: 'Non assignée',
      emergencyPhone: 'Téléphone urgence',
      emergencyContact: 'Contact tuteur/parent',
      contactsTemplate: 'Modèle contacts',
      importContacts: 'Importer contacts',
      bulkContactsTitle: 'Mise à jour des contacts par lot',
      downloadTemplate: 'Télécharger le modèle',
      uploadContactsFile: 'Uploader le fichier rempli',
      bulkUpdateSuccess: 'Contacts mis à jour avec succès',
      bulkUpdateError: 'Erreur lors de la mise à jour des contacts',
      contactsTemplateHelp: 'Téléchargez le modèle, remplissez les contacts, puis uploadez le fichier.',
      classFilter: 'Classe (optionnel)',
      noFileSelected: 'Aucun fichier sélectionné',
      fileSelected: 'Fichier sélectionné',
      // ─── Nouveautés V9 ───
      quickEdit: 'Renommer',
      nameDuplicated: 'Nom dupliqué (Nom = Prénom)',
      repairNames: 'Réparer noms',
      repairTitle: 'Réparation des noms dupliqués',
      repairDesc: "Cet outil détecte les élèves dont le nom complet a été enregistré DEUX FOIS (champs Nom et Prénom identiques). La réparation vide le champ Prénom et conserve le nom complet dans le champ Nom (last_name) uniquement — l'affichage reste identique.",
      repairScanning: 'Analyse de la base en cours...',
      repairFound: 'élève(s) avec nom dupliqué détecté(s)',
      repairNone: 'Aucun nom dupliqué trouvé. Toutes les données sont propres !',
      repairAll: 'Tout sélectionner',
      repairSelected: 'Réparer la sélection',
      repairRunning: 'Réparation en cours...',
      repairDone: 'Réparation terminée',
      repairClose: 'Fermer',
      repairFail: 'échec(s)',
      repairRepaired: 'corrigé',
      nameRequired: 'Le nom ou le prénom est requis',
      matriculeRequired: 'Le matricule est requis',
      matriculeExists: 'Ce matricule est déjà utilisé par un autre élève',
      dupFullNameHint: 'Nom complet → Nom',
      // ─── Gestion des doublons ───
      dupTitle: 'Gestion des doublons',
      dupDesc: "Détecte les élèves en double (même nom + même date de naissance). Pour chaque groupe, l'enregistrement à GARDER est coché par radio : les anciens matricules (format 2226-XXX) sont proposés à la suppression par défaut. Vérifiez puis supprimez ou désactivez les doublons.",
      dupScanning: 'Analyse de la base en cours (toutes les années)...',
      dupNone: 'Aucun doublon trouvé. La base est propre !',
      dupGroups: 'groupe(s) de doublons',
      dupKeep: 'Garder',
      dupDeleteSel: 'Supprimer la sélection',
      dupDeactivateSel: 'Désactiver la sélection',
      dupDeleted: 'Doublon(s) supprimé(s) définitivement',
      dupDeactivated: 'Doublon(s) désactivé(s)',
      dupConfirm: 'Supprimer DÉFINITIVEMENT les doublons sélectionnés ? Cette action est IRRÉVERSIBLE. Continuer ?',
      dupOldBadge: 'ancien',
      dupSelected: 'sélectionné(s)',
      dupRunning: 'Traitement en cours...',
      dupMatriculeCol: 'Matricule',
      dupClassCol: 'Classe',
      dupStatusCol: 'Statut',
      dupCreatedCol: 'Créé le',
      // ─── Modal de confirmation suppression ───
      dupConfirmTitle: 'Confirmer la suppression définitive',
      dupConfirmWarn: 'Les fiches ci-dessous seront EFFACÉES de la base de données, avec leur historique de classes et leurs inscriptions. Cette action est IRRÉVERSIBLE.',
      dupConfirmList: 'Fiche(s) qui seront supprimée(s)',
      dupConfirmBtn: 'Oui, supprimer définitivement',
      dupConfirmCount: 'fiche(s) concernée(s)',
      dupConfirmNote: 'Les fiches marquées « Garder » (bord verte) ne seront PAS touchées.',
    },
    en: {
      title: 'Student Management', subtitle: 'students registered',
      newStudent: 'New Student', search: 'Search...',
      advancedFilters: 'Advanced Filters', resetFilters: 'Reset filters',
      allSections: 'All sections', allCycles: 'All cycles',
      allLevels: 'All levels', allClasses: 'All classes',
      section: 'Section', cycle: 'Cycle', level: 'Level', class_: 'Class',
      status: 'Status', sex: 'Sex', handicap: 'Disability', socialCase: 'Social Case',
      totalStudents: 'Total students', active: 'Active',
      noData: 'No students found', loading: 'Loading...',
      noYear: 'No academic year selected',
      noYearMsg: 'Please select an academic year in the header',
      readonlyMsg: 'Read-only — modifications disabled',
      regNo: 'Reg. No.', name: 'Name', birth: 'Birth',
      contact: 'Contact', actions: 'Actions', view: 'View', edit: 'Edit', delete: 'Deactivate (INACTIVE)',
      exportCSV: 'Export CSV', exportExcel: 'Export Excel', exportPDF: 'Export PDF',
      yearChanged: 'Academic year changed — reloading data',
      contextUpdated: 'School context updated — reloading',
      errorLoading: 'Error loading students',
      deleteConfirm: 'Deactivate this student? This will change status to INACTIVE. Continue?',
      deleteSuccess: 'Student deactivated',
      deleteError: 'Deactivation error',
      filterByStructure: 'Filter by academic structure',
      activeFilters: 'Active filters:',
      readonlyTooltip: 'Only super_admin, admin, proviseur and censeur can modify',
      studentDetails: 'Student Details',
      editStudent: 'Edit Student',
      close: 'Close',
      save: 'Save',
      personalInfo: 'Personal Information',
      academicInfo: 'Academic Information',
      emergencySection: 'Emergency Contact',
      firstName: 'First Name',
      lastName: 'Last Name',
      dateOfBirth: 'Date of Birth',
      placeOfBirth: 'Place of Birth',
      nationality: 'Nationalité',
      sexe: 'Sex',
      phone: 'Phone',
      email: 'Email',
      address: 'Address',
      fatherName: 'Father Name',
      motherName: 'Mother Name',
      bloodGroup: 'Blood Group',
      allergies: 'Allergies',
      previousSchool: 'Previous School',
      enrollmentDate: 'Enrollment Date',
      enrollmentType: 'Enrollment Type',
      className: 'Class',
      levelName: 'Level',
      sectionName: 'Section',
      matricule: 'Registration No.',
      statusLabel: 'Status',
      handicapLabel: 'Disability',
      socialCaseLabel: 'Social Case',
      yes: 'Yes',
      no: 'No',
      male: 'Male',
      female: 'Female',
      cancel: 'Cancel',
      updateSuccess: 'Student updated',
      updateError: 'Update error',
      notAssigned: 'Not assigned',
      notAssignedLevel: 'Not assigned',
      notAssignedSection: 'Not assigned',
      emergencyPhone: 'Emergency phone',
      emergencyContact: 'Guardian/Parent contact',
      contactsTemplate: 'Contacts template',
      importContacts: 'Import contacts',
      bulkContactsTitle: 'Bulk contacts update',
      downloadTemplate: 'Download template',
      uploadContactsFile: 'Upload filled file',
      bulkUpdateSuccess: 'Contacts updated successfully',
      bulkUpdateError: 'Error updating contacts',
      contactsTemplateHelp: 'Download the template, fill in the contacts, then upload the file.',
      classFilter: 'Class (optional)',
      noFileSelected: 'No file selected',
      fileSelected: 'File selected',
      // ─── V9 additions ───
      quickEdit: 'Rename',
      nameDuplicated: 'Duplicated name (Last = First)',
      repairNames: 'Fix names',
      repairTitle: 'Fix duplicated names',
      repairDesc: 'This tool detects students whose full name was saved TWICE (identical Last and First name fields). The repair empties the First name field and keeps the full name in the Last name field only — the display stays identical.',
      repairScanning: 'Scanning database...',
      repairFound: 'student(s) with duplicated name detected',
      repairNone: 'No duplicated names found. All data is clean!',
      repairAll: 'Select all',
      repairSelected: 'Fix selection',
      repairRunning: 'Fixing in progress...',
      repairDone: 'Repair completed',
      repairClose: 'Close',
      repairFail: 'failed',
      repairRepaired: 'fixed',
      nameRequired: 'Name or first name is required',
      matriculeRequired: 'Registration number is required',
      matriculeExists: 'This registration number is already used by another student',
      dupFullNameHint: 'Full name → Last name',
      // ─── Duplicates management ───
      dupTitle: 'Duplicates Management',
      dupDesc: "Detects duplicate students (same name + same date of birth). For each group, the record to KEEP is selected by radio: old-format matricules (2226-XXX) are proposed for deletion by default. Review, then delete or deactivate the duplicates.",
      dupScanning: 'Scanning database (all academic years)...',
      dupNone: 'No duplicates found. Database is clean!',
      dupGroups: 'duplicate group(s)',
      dupKeep: 'Keep',
      dupDeleteSel: 'Delete selection',
      dupDeactivateSel: 'Deactivate selection',
      dupDeleted: 'Duplicate(s) permanently deleted',
      dupDeactivated: 'Duplicate(s) deactivated',
      dupConfirm: 'Permanently DELETE the selected duplicates? This action is IRREVERSIBLE. Continue?',
      dupOldBadge: 'old',
      dupSelected: 'selected',
      dupRunning: 'Processing...',
      dupMatriculeCol: 'Reg. No.',
      dupClassCol: 'Class',
      dupStatusCol: 'Status',
      dupCreatedCol: 'Created',
      // ─── Delete confirmation modal ───
      dupConfirmTitle: 'Confirm Permanent Deletion',
      dupConfirmWarn: 'The records below will be ERASED from the database, along with their class history and enrollments. This action is IRREVERSIBLE.',
      dupConfirmList: 'Record(s) that will be deleted',
      dupConfirmBtn: 'Yes, delete permanently',
      dupConfirmCount: 'record(s) affected',
      dupConfirmNote: 'Records marked "Keep" (green border) will NOT be touched.',
    }
  }[lang]), [lang])

  // Écoute changement d'année
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail?.year_id) return
      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null
      if (newYearId) {
        setSelectedAcademicYearId(newYearId)
        if (detail.label) setCurrentAcademicYear({ id: newYearId, name: detail.label })
      }
      if (newSchoolId !== null && !isNaN(newSchoolId)) setSchoolId(newSchoolId)
      setFilters({ search: '', section_id: '', cycle_id: '', level_id: '', class_id: '', status: '', sex: '', handicap: '', social_case: '' })
      setPage(1)
      setStudents([])
      toast.info($t.yearChanged)
    }
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId) {
          setSelectedAcademicYearId(newYearId)
          const label = getSelectedYearLabel()
          if (label) setCurrentAcademicYear({ id: newYearId, name: label })
          setPage(1)
          setStudents([])
        }
      }
    }
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [$t.yearChanged])

  // Sync au montage
  useEffect(() => {
    const yearId = getSelectedYearId()
    const yearLabel = getSelectedYearLabel()
    if (yearId) {
      setSelectedAcademicYearId(yearId)
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel })
    }
    try {
      const storedSchool = localStorage.getItem('lybibal-selected-school-id')
      if (storedSchool) { const sid = Number(storedSchool); if (!isNaN(sid)) setSchoolId(sid) }
    } catch (err) { }
  }, [])

  // Chargement des sections
  const loadSections = useCallback(async () => {
    if (!schoolId) { setSections([]); return }
    setRefLoading(true)
    try {
      let secData: any[] = []
      try {
        const res = await schoolApi.getSections(schoolId)
        secData = Array.isArray(res) ? res : (res?.data || res?.items || res?.results || [])
      } catch (e1) {
        try {
          const schools = await schoolApi.getSchools()
          const schoolsList = Array.isArray(schools) ? schools : (schools?.data || [])
          const activeSchool = schoolsList.find((s: any) => s.id === schoolId || s.status === 'active')
          if (activeSchool) {
            const res2 = await schoolApi.getSections(activeSchool.id)
            secData = Array.isArray(res2) ? res2 : (res2?.data || res2?.items || [])
          }
        } catch (e2) { }
      }
      setSections(secData)
    } catch (e) {
      setSections([])
    } finally {
      setRefLoading(false)
    }
  }, [schoolId])

  // Chargement des cycles, niveaux, classes
  const loadRefData = useCallback(async () => {
    if (!schoolId) { setCycles([]); setLevels([]); setClasses([]); return }
    try {
      const [cycData, levData, clsData] = await Promise.allSettled([
        cyclesApi.getAll(),
        levelsApi.getAll(),
        classesApi.getAll()
      ])
      setCycles(cycData.status === 'fulfilled' ? (Array.isArray(cycData.value) ? cycData.value : (cycData.value?.data || [])) : [])
      setLevels(levData.status === 'fulfilled' ? (Array.isArray(levData.value) ? levData.value : (levData.value?.data || [])) : [])
      setClasses(clsData.status === 'fulfilled' ? (Array.isArray(clsData.value) ? clsData.value : (clsData.value?.data || [])) : [])
    } catch (e) { }
  }, [schoolId])

  useEffect(() => {
    if (schoolId) {
      loadSections()
      loadRefData()
    }
  }, [schoolId, loadSections, loadRefData])

  // Filtrage cascadé
  const filteredCycles = useMemo(() => {
    if (!filters.section_id) return []
    return cycles.filter((c: Cycle) => c.section_id === Number(filters.section_id))
  }, [cycles, filters.section_id])

  const filteredLevels = useMemo(() => {
    if (!filters.cycle_id) {
      if (!filters.section_id) return []
      return levels.filter((l: Level) => {
        const c = cycles.find((cc: Cycle) => cc.id === l.cycle_id)
        return c && c.section_id === Number(filters.section_id)
      })
    }
    return levels.filter((l: Level) => l.cycle_id === Number(filters.cycle_id))
  }, [levels, cycles, filters.cycle_id, filters.section_id])

  const filteredClasses = useMemo(() => {
    if (!filters.level_id) {
      if (!filters.cycle_id) return []
      return classes.filter((c: ClassItem) => {
        const lvl = levels.find((l: Level) => l.id === c.level_id)
        return lvl && lvl.cycle_id === Number(filters.cycle_id)
      })
    }
    return classes.filter((c: ClassItem) => c.level_id === Number(filters.level_id))
  }, [classes, levels, filters.level_id, filters.cycle_id])

  // CHARGEMENT DES ÉLÈVES — VERSION CORRIGÉE AVEC LOGS DÉTAILLÉS
  const loadStudents = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId) {
      console.log('[StudentsPage] Skip load: missing schoolId or yearId', { schoolId, selectedAcademicYearId })
      setStudents([]); setTotal(0); return
    }

    setLoading(true)
    console.log('[StudentsPage] ====== LOADING STUDENTS ======')

    try {
      const params: Record<string, any> = {
        page: page,
        per_page: perPage,
        school_id: schoolId,
        academic_year_id: selectedAcademicYearId,
      }

      if (filters.search?.trim()) {
        params.search = filters.search.trim()
      }

      if (filters.status) {
        const statusMap: Record<string, string> = {
          'nouveau': 'NOUVEAU', 'inscrit': 'INSCRIT', 'exclu': 'EXCLU',
          'demission': 'DEMISSION', 'renvoye': 'RENVOYE', 'reintegre': 'REINTEGRE',
          'transfere': 'TRANSFERE', 'INACTIVE': 'INACTIVE', 'ancien': 'ANCIEN',
          'diplome': 'diplome', 'active': 'active',
        }
        params.status = statusMap[filters.status] || filters.status.toUpperCase()
      } else {
        // V16 : par défaut, seuls les élèves actifs (INSCRIT/NOUVEAU) —
        // démissionnaires, anciens, exclus... exclus de la liste.
        // Un filtre statut explicite (filtres avancés) bypass ce comportement.
        params.active_only = true
      }

      if (filters.sex) {
        params.sex = filters.sex
      }

      if (filters.handicap) {
        params.handicap = filters.handicap === 'yes'
      }
      if (filters.social_case) {
        params.social_case = filters.social_case === 'yes'
      }

      if (filters.section_id) params.section_id = Number(filters.section_id)
      if (filters.cycle_id) params.cycle_id = Number(filters.cycle_id)
      if (filters.level_id) params.level_id = Number(filters.level_id)
      if (filters.class_id) params.class_id = Number(filters.class_id)

      // V10 : tri serveur — s'applique à TOUTE la liste filtrée (toutes les pages)
      params.sort_by = sortField
      params.sort_order = sortDir

      console.log('[StudentsPage] API params:', JSON.stringify(params, null, 2))

      const res = await studentsApi.getAll(params)
      console.log('[StudentsPage] Raw API response:', res)

      const { items, totalCount } = parseStudentsResponse(res)

      console.log(`[StudentsPage] Parsed: ${items.length} items, total=${totalCount}`)

      // Log détaillé des champs d'enrichissement pour le premier item
      if (items.length > 0) {
        const first = items[0]
        console.log('[StudentsPage] First item enrichment fields:', {
          id: first.id,
          matricule: first.matricule,
          first_name: first.first_name,
          class_name: first.class_name,
          class_id: first.class_id,
          level_name: first.level_name,
          level_id: first.level_id,
          section_name: first.section_name,
          section_id: first.section_id,
          cycle_name: first.cycle_name,
          cycle_id: first.cycle_id,
          status: first.status,
          sex: first.sex,
        })
      }

      setStudents(items)
      setTotal(totalCount)
      console.log('[StudentsPage] Loaded', items.length, 'students')
      if (items.length > 0) {
        console.log('[StudentsPage] First student photo_url:', items[0].photo_url)
      }
    } catch (err: any) {
      console.error('[StudentsPage] Error loading students:', err)
      console.error('[StudentsPage] Error response:', err.response?.data)
      toast.error($t.errorLoading)
      setStudents([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [schoolId, selectedAcademicYearId, page, perPage, filters, sortField, sortDir, $t.errorLoading])

  useEffect(() => {
    console.log('[StudentsPage] Triggering loadStudents due to dependency change')
    loadStudents()
  }, [loadStudents])

  // V13 : chargement des totaux réels via apiService — même instance axios que
  // le reste de l'app : le token est ajouté par l'intercepteur (plus de 401).
  const loadStatsCards = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId) { setStatsCards(null); return }
    try {
      const res: any = await apiService.get('/students/stats/cards', {
        params: { school_id: schoolId, academic_year_id: selectedAcademicYearId },
      })
      const json = res?.data ?? res
      if (json?.success && json?.data) setStatsCards(json.data)
    } catch (e) { console.error('[StatsCards]', e) }
  }, [schoolId, selectedAcademicYearId])

  useEffect(() => { loadStatsCards() }, [loadStatsCards])

  // ═══════════════════════════════════════════════════════════════
  // ÉDITION INLINE DU NOM (2e bouton dans la colonne Actions)
  // ═══════════════════════════════════════════════════════════════
  const handleInlineEdit = (student: Student) => {
    if (isReadonly) { toast.warning($t.readonlyMsg); return }
    if (inlineEditId === student.id) { setInlineEditId(null); return }
    setInlineMatricule(student.matricule || '')
    if (isDuplicatedName(student)) {
      // Nom dupliqué : le champ Prénom est vidé, le nom complet reste dans Nom
      setInlineLastName(student.last_name)
      setInlineFirstName('')
    } else {
      setInlineLastName(student.last_name || '')
      setInlineFirstName(student.first_name || '')
    }
    setInlineEditId(student.id)
  }

  const handleInlineCancel = () => {
    setInlineEditId(null)
    setInlineMatricule('')
    setInlineLastName('')
    setInlineFirstName('')
  }

  const handleInlineSave = async (student: Student) => {
    const matricule = inlineMatricule.trim()
    const first = inlineFirstName.trim()
    const last = inlineLastName.trim()
    if (!matricule) { toast.error($t.matriculeRequired); return }
    if (!first && !last) { toast.error($t.nameRequired); return }
    setInlineSaving(true)
    try {
      const payload: Record<string, any> = isDuplicatedName(student)
        ? { first_name: '', last_name: last }
        : { first_name: first, last_name: last }
      // Matricule inclus seulement s'il a changé (le backend vérifie l'unicité)
      if (matricule !== student.matricule) {
        payload.matricule = matricule
      }
      try {
        await studentsApi.update(student.id, payload as any)
      } catch (e1) {
        // Fallback si first_name NOT NULL en base : on envoie null
        if (payload.first_name === '') {
          await studentsApi.update(student.id, { ...payload, first_name: null } as any)
        } else {
          throw e1
        }
      }
      toast.success($t.updateSuccess)
      setInlineEditId(null)
      setInlineMatricule('')
      setInlineLastName('')
      setInlineFirstName('')
      loadStudents()
    } catch (err: any) {
      console.error('[InlineEdit] save error:', err)
      // 400 "Ce matricule existe déjà" renvoyé par le backend
      const detail = err?.response?.data?.detail || ''
      if (typeof detail === 'string' && detail.toLowerCase().includes('matricule')) {
        toast.error($t.matriculeExists)
      } else {
        toast.error($t.updateError)
      }
    } finally {
      setInlineSaving(false)
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // RÉPARATION EN MASSE DES NOMS DUPLIQUÉS
  // ═══════════════════════════════════════════════════════════════
  const scanDuplicates = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId) return
    setRepairScanning(true)
    setRepairScanned(false)
    setRepairList([])
    setRepairProgress({ done: 0, total: 0, failed: 0 })
    try {
      const all: Student[] = []
      let pageN = 1
      // On pagine pour récupérer TOUS les élèves (jusqu'à 12 000)
      while (pageN <= 60) {
        const res = await studentsApi.getAll({
          page: pageN,
          per_page: 200,
          school_id: schoolId,
          academic_year_id: selectedAcademicYearId,
        })
        const { items, totalCount } = parseStudentsResponse(res)
        if (!items.length) break
        all.push(...items)
        if (all.length >= totalCount) break
        pageN++
      }
      const dups = all.filter(isDuplicatedName)
      console.log(`[Repair] Scanned ${all.length} students, ${dups.length} duplicated names`)
      setRepairList(dups.map(s => ({ student: s, name: s.first_name, checked: true })))
      setRepairScanned(true)
    } catch (e) {
      console.error('[Repair] scan error:', e)
      toast.error($t.errorLoading)
      setRepairScanned(true)
    } finally {
      setRepairScanning(false)
    }
  }, [schoolId, selectedAcademicYearId, $t.errorLoading])

  // Scan automatique à l'ouverture du modal
  useEffect(() => {
    if (repairModalOpen) scanDuplicates()
  }, [repairModalOpen, scanDuplicates])

  const toggleAllRepair = (checked: boolean) => {
    setRepairList(prev => prev.map(r => ({ ...r, checked })))
  }

  const updateRepairName = (studentId: number, name: string) => {
    setRepairList(prev => prev.map(r => r.student.id === studentId ? { ...r, name } : r))
  }

  const runRepair = async () => {
    const targets = repairList.filter(r => r.checked && !r.repaired && r.name.trim())
    if (!targets.length) return
    setRepairRunning(true)
    setRepairProgress({ done: 0, total: targets.length, failed: 0 })
    let failed = 0
    for (const item of targets) {
      try {
        try {
          await studentsApi.update(item.student.id, { last_name: item.name.trim(), first_name: '' } as any)
        } catch (e1) {
          // Fallback si first_name NOT NULL : on envoie null
          await studentsApi.update(item.student.id, { last_name: item.name.trim(), first_name: null } as any)
        }
        setRepairList(prev => prev.map(r => r.student.id === item.student.id ? { ...r, repaired: true, checked: false } : r))
      } catch (e) {
        failed++
        console.error('[Repair] failed for student', item.student.id, e)
      }
      setRepairProgress(prev => ({ ...prev, done: prev.done + 1, failed }))
    }
    setRepairRunning(false)
    toast.success($t.repairDone)
    loadStudents()
  }

  // ═══════════════════════════════════════════════════════════════
  // GESTION DES DOUBLONS — détection nom + date de naissance
  // ═══════════════════════════════════════════════════════════════
  const normalizeForDup = (s?: string) =>
    (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase()

  const OLD_MATRICULE_RE = /^\d{4}-\d{3}$/  // ex: 2226-395

  const scanDuplicatesByName = useCallback(async () => {
    if (!schoolId) return
    setDupScanning(true)
    setDupScanned(false)
    setDupGroups([])
    setDupProgress({ done: 0, total: 0, failed: 0 })
    try {
      // V15 : portée — toute l'école OU liste affichée (filtres: classe, année, recherche...)
      const baseParams: Record<string, any> = { school_id: schoolId }
      if (dupScope === 'displayed') {
        if (selectedAcademicYearId) baseParams.academic_year_id = selectedAcademicYearId
        if (filters.search?.trim()) baseParams.search = filters.search.trim()
        if (filters.status) baseParams.status = filters.status
        else baseParams.active_only = true
        if (filters.sex) baseParams.sex = filters.sex
        if (filters.section_id) baseParams.section_id = Number(filters.section_id)
        if (filters.cycle_id) baseParams.cycle_id = Number(filters.cycle_id)
        if (filters.level_id) baseParams.level_id = Number(filters.level_id)
        if (filters.class_id) baseParams.class_id = Number(filters.class_id)
      }
      const all: Student[] = []
      let pageN = 1
      while (pageN <= 60) {
        const res = await studentsApi.getAll({ ...baseParams, page: pageN, per_page: 200 })
        const { items, totalCount } = parseStudentsResponse(res)
        if (!items.length) break
        all.push(...items)
        if (all.length >= totalCount) break
        pageN++
      }
      // V15 : regroupement — 'strict' (nom + date naissance) ou 'name' (nom seul)
      const map = new Map<string, Student[]>()
      for (const s of all) {
        const fullName = `${s.last_name || ''} ${s.first_name || ''}`.trim()
        if (!fullName) continue
        const norm = normalizeForDup(fullName)
        const key = dupMode === 'strict' ? `${norm}|${s.date_of_birth || ''}` : norm
        if (!map.has(key)) map.set(key, [])
        map.get(key)!.push(s)
      }
      const groups: any[] = []
      map.forEach((members, key) => {
        if (members.length < 2) return
        // Gardien suggéré : matricule format récent d'abord, sinon le plus récent (created_at)
        const sorted = [...members].sort((a, b) => {
          const aOld = OLD_MATRICULE_RE.test(a.matricule || '') ? 1 : 0
          const bOld = OLD_MATRICULE_RE.test(b.matricule || '') ? 1 : 0
          if (aOld !== bOld) return aOld - bOld
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        })
        const keepId = sorted[0].id
        const fullName = `${members[0].last_name || ''} ${members[0].first_name || ''}`.trim()
        groups.push({
          key,
          fullName,
          dateOfBirth: members[0].date_of_birth || null,
          keepId,
          members: sorted.map(m => ({ student: m, checked: m.id !== keepId })),
        })
      })
      groups.sort((a, b) => a.fullName.localeCompare(b.fullName, 'fr'))
      console.log(`[Dup] ${all.length} élèves scannés → ${groups.length} groupe(s) de doublons`)
      setDupGroups(groups)
      setDupScanned(true)
    } catch (e) {
      console.error('[Dup] scan error:', e)
      toast.error($t.errorLoading)
      setDupScanned(true)
    } finally {
      setDupScanning(false)
    }
  }, [schoolId, selectedAcademicYearId, filters, dupScope, dupMode, $t.errorLoading])

  useEffect(() => { if (dupModalOpen) scanDuplicatesByName() }, [dupModalOpen, scanDuplicatesByName])

  // V15 : re-scan automatique quand on change portée ou critère (après 1er scan)
  useEffect(() => {
    if (dupModalOpen && dupScanned && !dupScanning && !dupRunning) scanDuplicatesByName()
  }, [dupScope, dupMode])

  const toggleDupMember = (groupKey: string, studentId: number) => {
    setDupGroups(prev => prev.map(g => g.key !== groupKey ? g : {
      ...g,
      members: g.members.map((m: any) => m.student.id === studentId ? { ...m, checked: !m.checked } : m),
    }))
  }

  const setDupKeeper = (groupKey: string, studentId: number) => {
    setDupGroups(prev => prev.map(g => g.key !== groupKey ? g : {
      ...g,
      keepId: studentId,
      // Le gardien ne peut pas être coché pour suppression
      members: g.members.map((m: any) => ({ ...m, checked: m.student.id !== studentId })),
    }))
  }

  // Sélection automatique : tous les anciens matricules (format 2226-XXX) sauf gardien
  const autoSelectOldMatricules = () => {
    setDupGroups(prev => prev.map(g => ({
      ...g,
      members: g.members.map((m: any) => ({
        ...m,
        checked: m.student.id !== g.keepId && OLD_MATRICULE_RE.test(m.student.matricule || ''),
      })),
    })))
  }

  const dupSelectedCount = dupGroups.reduce((acc, g) => acc + g.members.filter((m: any) => m.checked).length, 0)

  // Collecte les fiches cochées (hors gardiens) dans tous les groupes
  const collectDupTargets = (): Student[] => {
    const targets: Student[] = []
    for (const g of dupGroups) {
      for (const m of g.members) {
        if (m.checked && m.student.id !== g.keepId) targets.push(m.student)
      }
    }
    return targets
  }

  // Action : 'delete' = suppression définitive (endpoint /bulk-delete)
  //          'deactivate' = passage INACTIVE (sans confirmation, réversible)
  const runDupAction = async (mode: 'delete' | 'deactivate') => {
    const targets = collectDupTargets()
    if (!targets.length) {
      toast.warning(lang === 'fr' ? 'Aucun doublon sélectionné' : 'No duplicate selected')
      return
    }
    if (mode === 'delete') {
      // V11 : modal de confirmation détaillé au lieu de window.confirm natif
      setDupConfirmModal({ open: true, targets })
      return
    }
    await executeDupAction('deactivate', targets)
  }

  // Exécution réelle (appelée après confirmation du modal pour 'delete')
  const executeDupAction = async (mode: 'delete' | 'deactivate', targets: Student[]) => {
    setDupConfirmModal({ open: false, targets: [] })
    setDupRunning(true)
    setDupProgress({ done: 0, total: targets.length, failed: 0 })
    let failed = 0
    const failDetails: string[] = []
    const doneIds = new Set<number>()
    for (const t of targets) {
      try {
        if (mode === 'delete') {
          await apiDeleteStudents([t.id])
        } else {
          // V14 : ANCIEN = désactivé (enum studentstatus, voir note ci-dessus)
          await studentsApi.update(t.id, { status: 'ANCIEN' } as any)
        }
        doneIds.add(t.id)
      } catch (e: any) {
        failed++
        const msg = e?.message || String(e)
        failDetails.push(`${t.matricule}: ${msg}`)
        console.error(`[Dup] ${mode} failed for ${t.id} (${t.matricule}):`, e)
      }
      setDupProgress(prev => ({ ...prev, done: prev.done + 1, failed }))
    }
    // Retirer les membres traités des groupes
    setDupGroups(prev => prev
      .map(g => ({ ...g, members: g.members.filter((m: any) => !doneIds.has(m.student.id)) }))
      .filter(g => g.members.length > 1))
    setDupRunning(false)
    // V12 : feedback HONNÊTE — plus de faux succès
    const succeeded = targets.length - failed
    if (failed === 0) {
      toast.success(mode === 'delete' ? $t.dupDeleted : $t.dupDeactivated)
    } else if (succeeded === 0) {
      toast.error(lang === 'fr'
        ? `ÉCHEC TOTAL : les ${failed} suppressions ont échoué. Cause probable : endpoint /students/bulk-delete absent → remplacez students.py et redémarrez uvicorn. Détails : ${failDetails[0]}`
        : `TOTAL FAILURE: all ${failed} deletions failed. /students/bulk-delete endpoint missing — update students.py and restart uvicorn. Details: ${failDetails[0]}`)
    } else {
      toast.warning(lang === 'fr'
        ? `${succeeded} supprimé(s), ${failed} échec(s) : ${failDetails.slice(0, 3).join(' | ')}`
        : `${succeeded} deleted, ${failed} failed: ${failDetails.slice(0, 3).join(' | ')}`)
    }
    loadStudents()
    loadStatsCards()
  }

  // V13 : suppression via apiService (intercepteur auth = plus de 401 silencieux).
  // Lève une erreur si le backend signale un échec dans la réponse.
  const apiDeleteStudents = async (ids: number[]) => {
    const res: any = await apiService.post('/students/bulk-delete', { student_ids: ids })
    const json = res?.data ?? res
    if (json && json.success === false) throw new Error(json.detail || 'Erreur serveur')
    if (json && Array.isArray(json.failed) && json.failed.length > 0) {
      throw new Error(json.failed[0]?.error || 'Échec de la suppression')
    }
    return json
  }

  // Gestionnaires filtres cascadés
  const handleSectionChange = (value: string) => {
    setFilters(prev => ({ ...prev, section_id: value, cycle_id: '', level_id: '', class_id: '' }))
    setPage(1)
  }
  const handleCycleChange = (value: string) => {
    setFilters(prev => ({ ...prev, cycle_id: value, level_id: '', class_id: '' }))
    setPage(1)
  }
  const handleLevelChange = (value: string) => {
    setFilters(prev => ({ ...prev, level_id: value, class_id: '' }))
    setPage(1)
  }
  const handleClassChange = (value: string) => {
    setFilters(prev => ({ ...prev, class_id: value }))
    setPage(1)
  }

  // V10 : plus de tri client — le backend trie TOUTE la liste (sort_by/sort_order).
  // filteredStudents = données telles que renvoyées par le serveur, dans l'ordre.
  const filteredStudents = useMemo(() => students, [students])

  // V10 : bascule A-Z / Z-A sur le même champ, retour en page 1
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(1)
  }

  const handleView = (student: Student) => {
    console.log('[StudentsPage] handleView photo_url:', student.photo_url, 'resolved:', resolvePhotoUrl(student.photo_url))
    setSelectedStudent(student)
    setViewModalOpen(true)
  }

  const handleEdit = (student: Student) => {
    if (isReadonly) { toast.warning($t.readonlyMsg); return }
    setSelectedStudent(student)
    setEditForm({
      first_name: student.first_name, last_name: student.last_name,
      date_of_birth: student.date_of_birth, place_of_birth: student.place_of_birth,
      nationality: student.nationality, sex: student.sex,
      emergency_phone: student.emergency_phone, status: student.status,
      handicap: student.handicap, social_case: student.social_case,
      class_id: student.class_id, email: student.email, phone: student.phone,
      address: student.address, father_name: student.father_name,
      mother_name: student.mother_name, emergency_contact: student.emergency_contact,
      blood_group: student.blood_group, allergies: student.allergies,
      previous_school: student.previous_school,
      photo_url: student.photo_url, is_repeating: student.is_repeating,
      school_id: student.school_id ?? schoolId ?? undefined,
      academic_year_id: selectedAcademicYearId ?? undefined,
    })
    setEditPhotoFile(null)
    setEditPhotoPreview(resolvePhotoUrl(student.photo_url))
    setEditModalOpen(true)
  }

  const handleEditPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner une image' : 'Please select an image')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(lang === 'fr' ? "L'image ne doit pas dépasser 5 Mo" : 'Image must not exceed 5 MB')
      return
    }
    setEditPhotoFile(file)
    const reader = new FileReader()
    reader.onloadend = () => setEditPhotoPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const uploadEditPhoto = async (): Promise<string | null> => {
    if (!editPhotoFile) return editForm.photo_url || null
    setEditPhotoUploading(true)
    try {
      const res = await studentsApi.uploadPhoto(editPhotoFile)
      if (res?.success && res?.photo_url) {
        setEditForm(prev => ({ ...prev, photo_url: res.photo_url }))
        return res.photo_url
      }
      toast.error(lang === 'fr' ? 'Erreur upload photo' : 'Photo upload error')
      return null
    } catch (err) {
      console.error('Photo upload error:', err)
      toast.error(lang === 'fr' ? 'Erreur upload photo' : 'Photo upload error')
      return null
    } finally {
      setEditPhotoUploading(false)
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // CHANGEMENT DE CLASSE : l'endpoint PUT /students/{id} met à jour la
  // fiche élève mais PAS l'inscription (enrollment) — c'est elle qui
  // détermine la classe affichée dans la liste. On force donc la
  // synchronisation via les endpoints dédiés, dans l'ordre de préférence.
  // Renvoie true si l'un des endpoints a répondu avec succès.
  // ═══════════════════════════════════════════════════════════════
  // Endpoint RÉEL du backend (students.py) : POST /students/{id}/transfer.
  // Il met à jour l'enrollment de l'année, l'historique de classe ET le
  // class_id orphelin de la fiche élève. Fallback : PUT /students/{id} avec
  // l'année EXPLICITE (force get_or_create_enrollment sur la bonne année —
  // l'année résolue automatiquement côté backend peut être une autre année,
  // d'où le classe inchangé à l'affichage).
  const syncStudentClass = async (studentId: number, classId: number, prevClassId: number | null): Promise<boolean> => {
    const attempts: Array<{ method: 'post' | 'put'; url: string; data: Record<string, any> }> = [
      {
        method: 'post',
        url: `/students/${studentId}/transfer`,
        data: {
          from_class_id: prevClassId ?? undefined,
          to_class_id: classId,
          academic_year_id: selectedAcademicYearId ?? undefined,
          school_id: schoolId ?? undefined,
          reason: lang === 'fr' ? 'Changement de classe (fiche élève)' : 'Class change (student record)',
        },
      },
      {
        method: 'put',
        url: `/students/${studentId}`,
        data: {
          class_id: classId,
          academic_year_id: selectedAcademicYearId ?? undefined,
          school_id: schoolId ?? undefined,
        },
      },
    ]

    for (const att of attempts) {
      try {
        const res: any = await (apiService as any)[att.method](att.url, att.data)
        const json = res?.data ?? res
        if (json && json.success === false) continue
        console.log(`[StudentsPage] syncStudentClass OK via ${att.method.toUpperCase()} ${att.url}`)
        return true
      } catch (e) {
        console.warn(`[StudentsPage] syncStudentClass échec ${att.method.toUpperCase()} ${att.url}:`, e)
      }
    }
    return false
  }

  const handleSaveEdit = async () => {
    if (!selectedStudent || !editForm) return
    setEditLoading(true)
    try {
      let photoUrl: string | null = editForm.photo_url || null
      if (editPhotoFile) {
        photoUrl = await uploadEditPhoto()
        if (!photoUrl && editPhotoFile) {
          setEditLoading(false)
          return
        }
      }
      // V8 FIX: inclure school_id et academic_year_id pour que le backend puisse créer l'enrollment
      const payload: Record<string, any> = { ...editForm, photo_url: photoUrl }
      if (schoolId && !payload.school_id) payload.school_id = schoolId
      if (selectedAcademicYearId && !payload.academic_year_id) payload.academic_year_id = selectedAcademicYearId
      console.log('[StudentsPage] Update payload:', payload)
      // Détection AVANT l'appel : la classe ne se met pas à jour via
      // l'endpoint update seul -> on force la synchro enrollment ensuite.
      const prevClassId = selectedStudent.class_id ?? null
      const newClassId = editForm.class_id ?? null
      await studentsApi.update(selectedStudent.id, payload as any)

      // Forcer la mise à jour de la classe si elle a changé
      if (newClassId && newClassId !== prevClassId) {
        const synced = await syncStudentClass(selectedStudent.id, newClassId, prevClassId)
        if (!synced) {
          toast.warning(lang === 'fr'
            ? "Les informations sont enregistrées, mais la classe n'a pas pu être modifiée. Vérifiez que l'endpoint de changement de classe existe côté backend."
            : 'Information saved, but the class could not be changed. Check that the class-change endpoint exists on the backend.')
        }
      }

      toast.success($t.updateSuccess)
      setEditModalOpen(false)
      setSelectedStudent(null)
      setEditPhotoFile(null)
      setEditPhotoPreview(null)
      loadStudents()
      loadStatsCards()
    } catch (err) { toast.error($t.updateError) }
    finally { setEditLoading(false) }
  }

  const handleDeactivateClick = (id: number) => {
    if (isReadonly) { toast.warning($t.readonlyMsg); return }
    setDeactivateModal({ open: true, studentId: id })
  }

  const handleConfirmDeactivate = async () => {
    if (!deactivateModal.studentId) return
    try {
      // V14 : ANCIEN = statut « désactivé » dans votre enum studentstatus
      // (valeurs possibles : NOUVEAU, ANCIEN, INSCRIT, DIPLOME — pas d'INACTIVE).
      await studentsApi.update(deactivateModal.studentId, { status: 'ANCIEN' } as any)
      toast.success($t.deleteSuccess)
      setDeactivateModal({ open: false, studentId: null })
      loadStudents()
      loadStatsCards()
    } catch { toast.error($t.deleteError) }
  }

  // ─── Bulk contacts handlers ───
  const handleDownloadContactsTemplate = async () => {
    try {
      const params: Record<string, any> = { school_id: schoolId, academic_year_id: selectedAcademicYearId }
      if (contactsClassId) params.class_id = Number(contactsClassId)
      const blob = await studentsApi.getContactsTemplate(params)
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = `modele_contacts_${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success(lang === 'fr' ? 'Modèle téléchargé' : 'Template downloaded')
    } catch (err: any) {
      console.error('Template download error:', err)
      toast.error(lang === 'fr' ? 'Erreur téléchargement modèle' : 'Template download error')
    }
  }

  const handleContactsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner un fichier CSV' : 'Please select a CSV file')
      return
    }
    setContactsFile(file)
  }

  const handleImportContacts = async () => {
    if (!contactsFile) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner un fichier' : 'Please select a file')
      return
    }
    setContactsLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', contactsFile)
      const res = await studentsApi.importContacts(formData)
      if (res?.success) {
        toast.success(`${lang === 'fr' ? 'Contacts mis à jour' : 'Contacts updated'}: ${res.updated}`)
        setContactsModalOpen(false)
        setContactsFile(null)
        loadStudents()
      } else {
        toast.error(res?.detail || $t.bulkUpdateError)
      }
    } catch (err: any) {
      console.error('Import contacts error:', err)
      toast.error($t.bulkUpdateError)
    } finally {
      setContactsLoading(false)
    }
  }

  // Récupère TOUTES les pages correspondant aux filtres actifs (pas seulement la page courante)
  const fetchAllFilteredForExport = useCallback(async (): Promise<Student[]> => {
    if (!schoolId || !selectedAcademicYearId) return []
    const baseParams: Record<string, any> = {
      school_id: schoolId,
      academic_year_id: selectedAcademicYearId,
    }
    if (filters.search?.trim()) baseParams.search = filters.search.trim()
    if (filters.status) {
      const statusMap: Record<string, string> = {
        'nouveau': 'NOUVEAU', 'inscrit': 'INSCRIT', 'exclu': 'EXCLU',
        'demission': 'DEMISSION', 'renvoye': 'RENVOYE', 'reintegre': 'REINTEGRE',
        'transfere': 'TRANSFERE', 'INACTIVE': 'INACTIVE', 'ancien': 'ANCIEN',
        'diplome': 'diplome', 'active': 'active',
      }
      baseParams.status = statusMap[filters.status] || filters.status.toUpperCase()
    } else {
      // V16 : exports (impression incluse) — élèves actifs uniquement par défaut
      baseParams.active_only = true
    }
    if (filters.sex) baseParams.sex = filters.sex
    if (filters.handicap) baseParams.handicap = filters.handicap === 'yes'
    if (filters.social_case) baseParams.social_case = filters.social_case === 'yes'
    if (filters.section_id) baseParams.section_id = Number(filters.section_id)
    if (filters.cycle_id) baseParams.cycle_id = Number(filters.cycle_id)
    if (filters.level_id) baseParams.level_id = Number(filters.level_id)
    if (filters.class_id) baseParams.class_id = Number(filters.class_id)

    const all: Student[] = []
    let exportPage = 1
    // Jusqu'à 50 pages de 200 = 10 000 élèves max par export
    while (exportPage <= 50) {
      const res = await studentsApi.getAll({ ...baseParams, page: exportPage, per_page: 200 })
      const { items, totalCount } = parseStudentsResponse(res)
      if (!items.length) break
      all.push(...items)
      if (all.length >= totalCount) break
      exportPage++
    }
    console.log('[Export] Total élèves récupérés pour export:', all.length)
    return all
  }, [schoolId, selectedAcademicYearId, filters])

  // Construit le sous-titre du PDF avec les filtres actifs (ex: "Classe: 3ème A1")
  const buildExportSubtitle = (): string => {
    const parts: string[] = []
    if (filters.class_id) {
      const cls = classes.find(c => c.id === Number(filters.class_id))
      parts.push(`${lang === 'fr' ? 'Classe' : 'Class'}: ${cls ? `${cls.name} (${cls.abbreviation})` : filters.class_id}`)
    } else if (filters.level_id) {
      const lvl = levels.find(l => l.id === Number(filters.level_id))
      if (lvl) parts.push(`${lang === 'fr' ? 'Niveau' : 'Level'}: ${lang === 'fr' ? lvl.label_fr : lvl.label_en}`)
    } else if (filters.section_id) {
      const sec = sections.find(s => s.id === Number(filters.section_id))
      if (sec) parts.push(`${lang === 'fr' ? 'Section' : 'Section'}: ${lang === 'fr' ? sec.label_fr : sec.label_en}`)
    }
    return parts.join('  |  ')
  }

  // Export : récupère TOUTES les pages filtrées puis exporte (ordre d'affichage conservé)
  const handleExport = async (type: 'csv' | 'excel' | 'pdf') => {
    if (!schoolId || !selectedAcademicYearId) {
      toast.warning($t.noYearMsg)
      return
    }
    const filename = `eleves_${new Date().toISOString().split('T')[0]}`
    setExporting(true)
    try {
      toast.info(lang === 'fr' ? "Préparation de l'export (toutes les pages)..." : "Preparing export (all pages)...")
      const raw = await fetchAllFilteredForExport()
      if (!raw.length) {
        toast.warning($t.noData)
        return
      }
      // Tri : par classe (ordonnancement demandé), puis par nom/prénom
      const collator = new Intl.Collator('fr', { sensitivity: 'base' })
      const data = [...raw].sort((a, b) => {
        const classCmp = collator.compare(a.class_name || '￿', b.class_name || '￿')
        if (classCmp !== 0) return classCmp
        const nameCmp = collator.compare(a.last_name || '', b.last_name || '')
        if (nameCmp !== 0) return nameCmp
        return collator.compare(a.first_name || '', b.first_name || '')
      })
      switch (type) {
        case 'csv': exportToCSV(data, filename); toast.success('Export CSV terminé'); break
        case 'excel': exportToExcel(data, filename); toast.success('Export Excel terminé'); break
        case 'pdf': exportToPDF(data, filename, buildExportSubtitle()); toast.success('Export PDF lancé'); break
      }
    } catch (err) {
      console.error('[Export] Erreur:', err)
      toast.error($t.errorLoading)
    } finally {
      setExporting(false)
    }
  }

  const totalPages = Math.ceil(total / perPage)

  // Helpers d'affichage avec fallback
  const getClassDisplayName = (student: Student): string => {
    if (student.class_name && student.class_name !== 'Non assignée') {
      return student.class_name
    }
    return lang === 'fr' ? 'Non assignée' : 'Not assigned'
  }

  const getLevelDisplayName = (student: Student): string => {
    if (student.level_name && student.level_name !== 'Non assigné') {
      return student.level_name
    }
    return lang === 'fr' ? 'Non assigné' : 'Not assigned'
  }

  const getSectionDisplayName = (student: Student): string => {
    if (student.section_name && student.section_name !== 'Non assignée') {
      return student.section_name
    }
    return lang === 'fr' ? 'Non assignée' : 'Not assigned'
  }

  const getCycleDisplayName = (student: Student): string => {
    if (student.cycle_name && student.cycle_name !== 'Non assigné') {
      return student.cycle_name
    }
    return lang === 'fr' ? 'Non assigné' : 'Not assigned'
  }

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-amber-50/20 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{$t.noYear}</h2>
          <p className="text-sm text-gray-500">{$t.noYearMsg}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-amber-50/20">
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 bg-clip-text text-transparent">{$t.title}</h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">{statsCards?.total ?? total} {$t.subtitle}</p>
              {currentAcademicYear && (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">{currentAcademicYear.name}</span>
                  <span className="text-xs text-gray-400">school_id: {schoolId || "-"}</span>
                  {isReadonly && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1" title={$t.readonlyTooltip}>
                      <AlertCircle className="w-3 h-3" />{$t.readonlyMsg}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('csv')} disabled={exporting} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" title="Export CSV (toutes les pages filtrées)">
                {exporting ? <Loader2 className="w-4 h-4 text-green-600 animate-spin" /> : <FileText className="w-4 h-4 text-green-600" />}<span className="hidden sm:inline">CSV</span>
              </button>
              <button onClick={() => handleExport('excel')} disabled={exporting} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" title="Export Excel (toutes les pages filtrées)">
                {exporting ? <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-600" />}<span className="hidden sm:inline">Excel</span>
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" title="Export PDF (toutes les pages filtrées)">
                {exporting ? <Loader2 className="w-4 h-4 text-red-600 animate-spin" /> : <FileDown className="w-4 h-4 text-red-600" />}<span className="hidden sm:inline">PDF</span>
              </button>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <button onClick={() => setContactsModalOpen(true)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm" title={$t.bulkContactsTitle}>
              <Phone className="w-4 h-4 text-blue-600" /><span className="hidden sm:inline">{$t.importContacts}</span>
            </button>
            {/* ─── NOUVEAU : Réparation en masse des noms dupliqués ─── */}
            {canModify && (
              <>
                <div className="w-px h-8 bg-gray-200" />
                <button onClick={() => setRepairModalOpen(true)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-amber-50 hover:border-amber-300 transition-all shadow-sm" title={$t.repairTitle}>
                  <Wrench className="w-4 h-4 text-amber-600" /><span className="hidden sm:inline">{$t.repairNames}</span>
                </button>
                <button onClick={() => setDupModalOpen(true)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:border-red-300 transition-all shadow-sm" title={$t.dupTitle}>
                  <Copy className="w-4 h-4 text-red-600" /><span className="hidden sm:inline">{$t.dupTitle}</span>
                </button>
              </>
            )}
            <div className="w-px h-8 bg-gray-200" />
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={filters.search} onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1) }} placeholder={$t.search}
                className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-48 lg:w-64" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border', showFilters ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}>
              <Filter className="w-4 h-4" /><span className="hidden sm:inline">{$t.advancedFilters}</span>
            </button>
            {canModify && (
              <button onClick={() => navigate('/students/create')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200">
                <UserPlus className="w-4 h-4" />{$t.newStudent}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Filtres cascadés */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Filter className="w-4 h-4 text-blue-600" />{$t.filterByStructure}</h3>
            <button onClick={() => { setFilters({ search: '', section_id: '', cycle_id: '', level_id: '', class_id: '', status: '', sex: '', handicap: '', social_case: '' }); setPage(1) }} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"><X className="w-3 h-3" />{$t.resetFilters}</button>
          </div>
          {refLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement des sections...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5"><School className="w-3.5 h-3.5 inline mr-1 text-blue-500" />{$t.section}</label>
                <select value={filters.section_id} onChange={(e) => handleSectionChange(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="">{$t.allSections}</option>
                  {sections.map((s: Section) => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
                </select>
                {sections.length === 0 && schoolId && <p className="text-xs text-amber-600 mt-1">Aucune section trouvée</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5"><GraduationCap className="w-3.5 h-3.5 inline mr-1 text-purple-500" />{$t.cycle}</label>
                <select value={filters.cycle_id} onChange={(e) => handleCycleChange(e.target.value)} disabled={!filters.section_id || filteredCycles.length === 0} className={cn('w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', (!filters.section_id || filteredCycles.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}>
                  <option value="">{$t.allCycles}</option>
                  {filteredCycles.map((c: Cycle) => (<option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5"><ArrowUpDown className="w-3.5 h-3.5 inline mr-1 text-amber-500" />{$t.level}</label>
                <select value={filters.level_id} onChange={(e) => handleLevelChange(e.target.value)} disabled={!filters.cycle_id || filteredLevels.length === 0} className={cn('w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', (!filters.cycle_id || filteredLevels.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}>
                  <option value="">{$t.allLevels}</option>
                  {filteredLevels.map((l: Level) => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5"><Users className="w-3.5 h-3.5 inline mr-1 text-emerald-500" />{$t.class_}</label>
                <select value={filters.class_id} onChange={(e) => handleClassChange(e.target.value)} disabled={!filters.level_id || filteredClasses.length === 0} className={cn('w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', (!filters.level_id || filteredClasses.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}>
                  <option value="">{$t.allClasses}</option>
                  {filteredClasses.map((c: ClassItem) => (<option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>))}
                </select>
              </div>
            </div>
          )}
          {(filters.section_id || filters.cycle_id || filters.level_id || filters.class_id) && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">{$t.activeFilters}</span>
              {filters.section_id && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">{$t.section}: {sections.find(s => s.id === Number(filters.section_id))?.label_fr}<button onClick={() => handleSectionChange('')} className="hover:text-blue-900"><X className="w-3 h-3" /></button></span>}
              {filters.cycle_id && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">{$t.cycle}: {filteredCycles.find(c => c.id === Number(filters.cycle_id))?.label_fr}<button onClick={() => handleCycleChange('')} className="hover:text-purple-900"><X className="w-3 h-3" /></button></span>}
              {filters.level_id && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">{$t.level}: {filteredLevels.find(l => l.id === Number(filters.level_id))?.label_fr}<button onClick={() => handleLevelChange('')} className="hover:text-amber-900"><X className="w-3 h-3" /></button></span>}
              {filters.class_id && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1">{$t.class_}: {filteredClasses.find(c => c.id === Number(filters.class_id))?.name}<button onClick={() => handleClassChange('')} className="hover:text-emerald-900"><X className="w-3 h-3" /></button></span>}
            </div>
          )}
        </div>

        {/* Filtres avancés */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Filter className="w-4 h-4" />{$t.advancedFilters}</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{$t.status}</label>
                <select value={filters.status} onChange={(e) => { setFilters(prev => ({ ...prev, status: e.target.value })); setPage(1) }} className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option>
                  <option value="nouveau">Nouveau</option><option value="inscrit">Inscrit</option><option value="exclu">Exclu</option>
                  <option value="demission">Démission</option><option value="renvoye">Renvoyé</option><option value="reintegre">Réintégré</option>
                  <option value="transfere">Transféré</option><option value="INACTIVE">Inactif</option><option value="ancien">Ancien</option>
                  <option value="diplome">Diplômé</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{$t.sex}</label>
                <select value={filters.sex} onChange={(e) => { setFilters(prev => ({ ...prev, sex: e.target.value })); setPage(1) }} className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option><option value="M">Masculin</option><option value="F">Féminin</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{$t.handicap}</label>
                <select value={filters.handicap} onChange={(e) => { setFilters(prev => ({ ...prev, handicap: e.target.value })); setPage(1) }} className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option><option value="yes">Oui</option><option value="no">Non</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{$t.socialCase}</label>
                <select value={filters.social_case} onChange={(e) => { setFilters(prev => ({ ...prev, social_case: e.target.value })); setPage(1) }} className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option><option value="yes">Oui</option><option value="no">Non</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-xl font-bold text-gray-900">{statsCards?.total ?? total}</p><p className="text-xs text-gray-500">{$t.totalStudents}</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-xl font-bold text-gray-900">{statsCards?.active ?? students.filter(s => ['NOUVEAU', 'INSCRIT'].includes(s.status)).length}</p><p className="text-xs text-gray-500">{$t.active}</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-xl font-bold text-gray-900">{statsCards?.handicaps ?? students.filter(s => s.handicap).length}</p><p className="text-xs text-gray-500">{$t.handicap}</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><HeartPulse className="w-5 h-5 text-purple-600" /></div>
            <div><p className="text-xl font-bold text-gray-900">{statsCards?.social_cases ?? students.filter(s => s.social_case).length}</p><p className="text-xs text-gray-500">{$t.socialCase}</p></div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Barre de recherche au-dessus du tableau */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1) }}
                placeholder={lang === 'fr' ? 'Rechercher un élève (nom, prénom, matricule)...' : 'Search a student (name, reg. no.)...'}
                className="pl-10 pr-8 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-full"
              />
              {filters.search && (
                <button onClick={() => { setFilters(prev => ({ ...prev, search: '' })); setPage(1) }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filteredStudents.length} / {total} {lang === 'fr' ? 'élève(s)' : 'student(s)'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('matricule')} className="flex items-center gap-1 hover:text-gray-700">
                      {$t.regNo}
                      {sortField === 'matricule'
                        ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />)
                        : <ArrowUpDown className="w-3 h-3 text-gray-300" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('last_name')} className="flex items-center gap-1 hover:text-gray-700">
                      {$t.name}
                      {sortField === 'last_name'
                        ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />)
                        : <ArrowUpDown className="w-3 h-3 text-gray-300" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">{lang === 'fr' ? 'Classe' : 'Class'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">{$t.birth}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">{$t.contact}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">{lang === 'fr' ? 'Redoublant' : 'Repeating'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{$t.status}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">{$t.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" /><p className="text-sm text-gray-500 mt-2">{$t.loading}</p></td></tr>
                ) : filteredStudents.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center"><Users className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">{$t.noData}</p></td></tr>
                ) : (
                  filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3"><span className="font-mono text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{student.matricule}</span></td>
                      <td className="px-4 py-3">
                        {/* ═══ ÉDITION INLINE : la cellule Nom devient éditable quand inlineEditId === student.id ═══ */}
                        {inlineEditId === student.id ? (
                          <div className="flex flex-col gap-1.5 min-w-[240px]" onClick={e => e.stopPropagation()}>
                            <input
                              type="text"
                              value={inlineMatricule}
                              onChange={(e) => setInlineMatricule(e.target.value)}
                              placeholder={$t.matricule}
                              title={$t.matricule}
                              className="px-2 py-1.5 bg-white border border-purple-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleInlineSave(student)
                                if (e.key === 'Escape') handleInlineCancel()
                              }}
                            />
                            <div className="grid grid-cols-2 gap-1.5">
                              <input
                                type="text"
                                value={inlineLastName}
                                onChange={(e) => setInlineLastName(e.target.value)}
                                placeholder={$t.lastName}
                                className="px-2 py-1.5 bg-white border border-purple-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlineSave(student)
                                  if (e.key === 'Escape') handleInlineCancel()
                                }}
                              />
                              <input
                                type="text"
                                value={inlineFirstName}
                                onChange={(e) => setInlineFirstName(e.target.value)}
                                placeholder={isDuplicatedName(student) ? $t.dupFullNameHint : $t.firstName}
                                className="px-2 py-1.5 bg-white border border-purple-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlineSave(student)
                                  if (e.key === 'Escape') handleInlineCancel()
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleInlineSave(student)}
                                disabled={inlineSaving}
                                className="flex items-center gap-1 px-2.5 py-1 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
                              >
                                {inlineSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                {$t.save}
                              </button>
                              <button
                                onClick={handleInlineCancel}
                                className="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                              >
                                {$t.cancel}
                              </button>
                              {isDuplicatedName(student) && (
                                <span className="text-[10px] text-amber-600 font-medium">{$t.dupFullNameHint}</span>
                              )}
                            </div>
                          </div>
                        ) : (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        {student.photo_url ? (
                          <img 
                            src={resolvePhotoUrl(student.photo_url) || ''} 
                            alt={`${student.first_name} ${student.last_name}`} 
                            className="w-full h-full object-cover"
                            onError={(e) => { console.error('[StudentsPage] Table img error:', (e.target as HTMLImageElement).src); (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold">{student.first_name?.[0]}{student.last_name?.[0]}</div>
                        )}
                      </div>
                          <div>
                            <p className="font-medium text-gray-900 flex items-center gap-1">
                              {student.last_name} {student.first_name}
                              {isDuplicatedName(student) && (
                                <span title={$t.nameDuplicated} className="inline-flex items-center">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">{student.sex === 'M' ? '♂' : '♀'} · {student.nationality || '-'}</p>
                          </div>
                        </div>
                        )}
                      </td>
                      {/* Affichage de la classe avec helper getClassDisplayName */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 text-gray-700 font-medium">
                            <GraduationCap className="w-3.5 h-3.5 text-blue-500" />
                            <span>{getClassDisplayName(student)}</span>
                            {student.is_repeating && (
                              <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full" title={lang === 'fr' ? 'Redouble' : 'Repeating'}>R</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <ArrowUpDown className="w-3 h-3" />
                            <span>{getLevelDisplayName(student)}</span>
                            {student.cycle_name && student.cycle_name !== 'Non assigné' && (
                              <span className="text-gray-300">· {getCycleDisplayName(student)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <School className="w-3 h-3" />
                            <span>{getSectionDisplayName(student)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="text-gray-600">
                          <p className="text-xs">{formatDate(student.date_of_birth)}</p>
                          <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{student.place_of_birth || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1 text-gray-600 text-xs"><Phone className="w-3.5 h-3.5 text-gray-400" />{student.emergency_phone || '-'}</div>
                          {student.emergency_contact && <div className="flex items-center gap-1 text-gray-500 text-xs"><User className="w-3 h-3 text-gray-400" />{student.emergency_contact}</div>}
                        </div>
                        {student.handicap && <span className="inline-flex items-center gap-1 text-xs text-amber-600 mt-1"><AlertCircle className="w-3 h-3" />Handicap</span>}
                        {student.social_case && <span className="inline-flex items-center gap-1 text-xs text-purple-600 mt-1"><HeartPulse className="w-3 h-3" />Cas social</span>}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {student.is_repeating ? (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full" title={lang === 'fr' ? 'Redouble' : 'Repeating'}>R</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(student.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => handleView(student)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600 transition-colors" title={$t.view}><Eye className="w-4 h-4" /></button>
                          {/* ─── NOUVEAU : 2e bouton d'édition = renommage inline du nom ─── */}
                          {canModify && (
                            <button
                              onClick={() => handleInlineEdit(student)}
                              className={cn(
                                'p-1.5 rounded-lg transition-colors',
                                inlineEditId === student.id
                                  ? 'bg-purple-100 text-purple-600'
                                  : 'hover:bg-purple-50 text-gray-400 hover:text-purple-600'
                              )}
                              title={$t.quickEdit}
                            >
                              <PenLine className="w-4 h-4" />
                            </button>
                          )}
                          {canModify && <button onClick={() => handleEdit(student)} className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-400 hover:text-amber-600 transition-colors" title={$t.edit}><Edit2 className="w-4 h-4" /></button>}
                          {canModify && <button onClick={() => handleDeactivateClick(student.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors" title={$t.delete}><Trash2 className="w-4 h-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-xs text-gray-500">{lang === 'fr' ? `Affichage ${(page - 1) * perPage + 1} - ${Math.min(page * perPage, total)} sur ${total}` : `Showing ${(page - 1) * perPage + 1} - ${Math.min(page * perPage, total)} of ${total}`}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm text-gray-600 min-w-[3rem] text-center">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Voir Détails */}
      {viewModalOpen && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setViewModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-white/20">
                  {selectedStudent.photo_url ? (
                    <img src={selectedStudent.photo_url} alt={`${selectedStudent.first_name} ${selectedStudent.last_name}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold">{selectedStudent.first_name?.[0]}{selectedStudent.last_name?.[0]}</div>
                  )}
                </div>
                <div><h3 className="text-white font-semibold text-lg">{$t.studentDetails}</h3><p className="text-blue-100 text-sm">{selectedStudent.matricule}</p></div>
              </div>
              <button onClick={() => setViewModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="p-6 space-y-6">
              {/* Photo de l'élève */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 flex-shrink-0">
                  {selectedStudent.photo_url ? (
                    <img 
                      src={resolvePhotoUrl(selectedStudent.photo_url) || ''} 
                      alt={`${selectedStudent.first_name} ${selectedStudent.last_name}`} 
                      className="w-full h-full object-cover"
                      onError={(e) => { console.error('[StudentsPage] Image load error:', (e.target as HTMLImageElement).src); (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-lg font-bold">
                      {selectedStudent.first_name?.[0]}{selectedStudent.last_name?.[0]}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    {selectedStudent.last_name} {selectedStudent.first_name}
                    {isDuplicatedName(selectedStudent) && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{$t.nameDuplicated}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500 font-mono">{selectedStudent.matricule}</p>
                  <div className="mt-1">{getStatusBadge(selectedStudent.status)}</div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-blue-500" />{$t.personalInfo}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.firstName}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.first_name}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.lastName}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.last_name || <span className="text-gray-400 italic">—</span>}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.sex}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.sex === 'M' ? $t.male : $t.female}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.dateOfBirth}</p><p className="text-sm font-medium text-gray-900">{formatDate(selectedStudent.date_of_birth)}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.placeOfBirth}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.place_of_birth || '-'}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.nationality}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.nationality || '-'}</p></div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-purple-500" />{$t.academicInfo}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.matricule}</p><p className="text-sm font-medium text-blue-600 font-mono">{selectedStudent.matricule}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.statusLabel}</p><div className="mt-1">{getStatusBadge(selectedStudent.status)}</div></div>
                  {/* ✅ CORRIGÉ: utilisation des helpers pour l'affichage dans le modal */}
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.className}</p><p className="text-sm font-medium text-gray-900">{getClassDisplayName(selectedStudent)}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.levelName}</p><p className="text-sm font-medium text-gray-900">{getLevelDisplayName(selectedStudent)}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.sectionName}</p><p className="text-sm font-medium text-gray-900">{getSectionDisplayName(selectedStudent)}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.enrollmentDate}</p><p className="text-sm font-medium text-gray-900">{formatDate(selectedStudent.created_at)}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                    <p className="text-xs text-gray-500">Redoublement</p>
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', selectedStudent.is_repeating ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500')}>
                      {selectedStudent.is_repeating ? 'Oui' : 'Non'}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-amber-500" />{$t.emergencySection}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.phone}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.emergency_phone || '-'}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.email}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.email || '-'}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.address}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.address || '-'}</p></div>
                  <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">{$t.emergencyContact}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.emergency_contact || '-'}</p></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3"><AlertCircle className="w-5 h-5 text-amber-500" /><div><p className="text-xs text-gray-500">{$t.handicapLabel}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.handicap ? $t.yes : $t.no}</p></div></div>
                <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3"><HeartPulse className="w-5 h-5 text-purple-500" /><div><p className="text-xs text-gray-500">{$t.socialCaseLabel}</p><p className="text-sm font-medium text-gray-900">{selectedStudent.social_case ? $t.yes : $t.no}</p></div></div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end">
              <button onClick={() => setViewModalOpen(false)} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">{$t.close}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier */}
      {editModalOpen && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setEditModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white text-sm font-bold"><Edit2 className="w-5 h-5" /></div>
                <div><h3 className="text-white font-semibold text-lg">{$t.editStudent}</h3><p className="text-amber-100 text-sm">{selectedStudent.matricule} — {selectedStudent.last_name} {selectedStudent.first_name}</p></div>
              </div>
              <button onClick={() => setEditModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="p-6 space-y-6">
              {/* Photo de l'élève */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                  {editPhotoPreview ? (
                    <img src={editPhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <User className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-all">
                    <FileText className="w-4 h-4 text-gray-400" />
                    {editPhotoFile ? (lang === 'fr' ? 'Changer la photo' : 'Change photo') : (lang === 'fr' ? 'Choisir une photo' : 'Choose a photo')}
                    <input type="file" accept="image/*" onChange={handleEditPhotoChange} className="hidden" />
                  </label>
                  {editPhotoFile && (
                    <p className="text-xs text-gray-500 mt-1">{editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(1)} Ko)</p>
                  )}
                  {editPhotoUploading && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{lang === 'fr' ? 'Upload en cours...' : 'Uploading...'}</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-blue-500" />{$t.personalInfo}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.firstName}</label><input type="text" value={editForm.first_name || ''} onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.lastName}</label><input type="text" value={editForm.last_name || ''} onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.sex}</label><select value={editForm.sex || ''} onChange={(e) => setEditForm(prev => ({ ...prev, sex: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"><option value="">{lang === 'fr' ? 'Choisir...' : 'Choose...'}</option><option value="M">{$t.male}</option><option value="F">{$t.female}</option></select></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.dateOfBirth}</label><input type="date" value={editForm.date_of_birth ? editForm.date_of_birth.split('T')[0] : ''} onChange={(e) => setEditForm(prev => ({ ...prev, date_of_birth: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.placeOfBirth}</label><input type="text" value={editForm.place_of_birth || ''} onChange={(e) => setEditForm(prev => ({ ...prev, place_of_birth: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.nationality}</label><input type="text" value={editForm.nationality || ''} onChange={(e) => setEditForm(prev => ({ ...prev, nationality: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-purple-500" />{$t.academicInfo}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.statusLabel}</label><select value={editForm.status || ''} onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"><option value="NOUVEAU">Nouveau</option><option value="INSCRIT">Inscrit</option><option value="EXCLU">Exclu</option><option value="DEMISSION">Démission</option><option value="RENVOYE">Renvoyé</option><option value="REINTEGRE">Réintégré</option><option value="TRANSFERE">Transféré</option><option value="INACTIVE">Inactif</option><option value="ANCIEN">Ancien</option></select></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.className}</label><select value={editForm.class_id || ''} onChange={(e) => setEditForm(prev => ({ ...prev, class_id: e.target.value ? Number(e.target.value) : undefined }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"><option value="">{lang === 'fr' ? 'Choisir une classe...' : 'Choose a class...'}</option>{classes.map((c: ClassItem) => (<option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>))}</select></div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-amber-500" />{$t.emergencySection}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.phone}</label><input type="text" value={editForm.emergency_phone || ''} onChange={(e) => setEditForm(prev => ({ ...prev, emergency_phone: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.email}</label><input type="email" value={editForm.email || ''} onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.address}</label><input type="text" value={editForm.address || ''} onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">{$t.emergencyContact}</label><input type="text" value={editForm.emergency_contact || ''} onChange={(e) => setEditForm(prev => ({ ...prev, emergency_contact: e.target.value }))} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3"><input type="checkbox" id="edit-handicap" checked={!!editForm.handicap} onChange={(e) => setEditForm(prev => ({ ...prev, handicap: e.target.checked }))} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" /><label htmlFor="edit-handicap" className="text-sm text-gray-700">{$t.handicapLabel}</label></div>
                <div className="flex items-center gap-3"><input type="checkbox" id="edit-social" checked={!!editForm.social_case} onChange={(e) => setEditForm(prev => ({ ...prev, social_case: e.target.checked }))} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" /><label htmlFor="edit-social" className="text-sm text-gray-700">{$t.socialCaseLabel}</label></div>
                <div className="flex items-center gap-3 md:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input type="checkbox" id="edit-repeating" checked={!!editForm.is_repeating} onChange={(e) => setEditForm(prev => ({ ...prev, is_repeating: e.target.checked }))} className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500" />
                  <label htmlFor="edit-repeating" className="text-sm text-gray-700 font-medium">
                    {lang === 'fr' ? 'Cet élève redouble la classe' : 'This student is repeating the class'}
                  </label>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">{$t.cancel}</button>
              <button onClick={handleSaveEdit} disabled={editLoading} className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 flex items-center gap-2">{editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{$t.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Désactivation */}
      {deactivateModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setDeactivateModal({ open: false, studentId: null })}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-white font-semibold text-lg flex items-center gap-2"><AlertCircle className="w-5 h-5" />{lang === 'fr' ? 'Confirmer la désactivation' : 'Confirm Deactivation'}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">{lang === 'fr' ? "Cette action changera le statut de cet élève à INACTIF. L'élève ne sera pas supprimé de la base de données, mais sera marqué comme inactif." : 'This action will change this student status to INACTIVE. The student will not be deleted from the database, but will be marked as inactive.'}</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-amber-700 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{lang === 'fr' ? "Cette action est réversible. Vous pourrez réactiver l'élève plus tard." : 'This action is reversible. You can reactivate the student later.'}</p>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeactivateModal({ open: false, studentId: null })} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
                <button onClick={handleConfirmDeactivate} className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-sm font-medium hover:from-red-600 hover:to-red-700 transition-all flex items-center gap-2"><Trash2 className="w-4 h-4" />{lang === 'fr' ? 'Désactiver' : 'Deactivate'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL RÉPARATION EN MASSE DES NOMS DUPLIQUÉS
          ═══════════════════════════════════════════════════════════ */}
      {repairModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !repairRunning && setRepairModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 rounded-t-2xl flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white"><Wrench className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{$t.repairTitle}</h3>
                  <p className="text-amber-100 text-xs">{lang === 'fr' ? 'Nom complet conservé dans Nom (last_name) uniquement' : 'Full name kept in Last name field only'}</p>
                </div>
              </div>
              <button onClick={() => !repairRunning && setRepairModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <p className="text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-lg p-3">{$t.repairDesc}</p>

              {/* État : analyse */}
              {repairScanning && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
                  <p className="text-sm text-gray-500">{$t.repairScanning}</p>
                </div>
              )}

              {/* État : analyse terminée, aucun doublon */}
              {!repairScanning && repairScanned && repairList.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{$t.repairNone}</p>
                </div>
              )}

              {/* État : doublons trouvés */}
              {!repairScanning && repairList.length > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {repairList.filter(r => !r.repaired).length} {$t.repairFound}
                    </p>
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={repairList.every(r => r.checked || r.repaired)}
                        onChange={(e) => toggleAllRepair(e.target.checked)}
                        className="w-3.5 h-3.5 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                      />
                      {$t.repairAll}
                    </label>
                  </div>

                  {/* Barre de progression */}
                  {repairRunning && (
                    <div className="space-y-1">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-amber-500 to-amber-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: repairProgress.total ? `${Math.round((repairProgress.done / repairProgress.total) * 100)}%` : '0%' }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-center">
                        {repairProgress.done} / {repairProgress.total}
                        {repairProgress.failed > 0 && <span className="text-red-600 font-medium"> — {repairProgress.failed} {$t.repairFail}</span>}
                      </p>
                    </div>
                  )}

                  {/* Liste des doublons */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                      {repairList.map((item) => (
                        <div key={item.student.id} className={cn('flex items-center gap-3 px-3 py-2', item.repaired ? 'bg-green-50/60' : 'hover:bg-gray-50')}>
                          <input
                            type="checkbox"
                            checked={item.checked}
                            disabled={item.repaired || repairRunning}
                            onChange={(e) => setRepairList(prev => prev.map(r => r.student.id === item.student.id ? { ...r, checked: e.target.checked } : r))}
                            className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500 shrink-0"
                          />
                          <span className="font-mono text-[11px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0 w-24 text-center truncate">{item.student.matricule}</span>
                          {item.repaired ? (
                            <span className="flex-1 text-sm text-gray-700 flex items-center gap-2">
                              {item.name}
                              <span className="text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                <Check className="w-2.5 h-2.5" />{$t.repairRepaired}
                              </span>
                            </span>
                          ) : (
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => updateRepairName(item.student.id, e.target.value)}
                              disabled={repairRunning}
                              className="flex-1 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 disabled:bg-gray-50"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end gap-3 shrink-0">
              <button
                onClick={() => setRepairModalOpen(false)}
                disabled={repairRunning}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {$t.repairClose}
              </button>
              <button
                onClick={runRepair}
                disabled={repairRunning || repairScanning || repairList.filter(r => r.checked && !r.repaired).length === 0}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {repairRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                {repairRunning ? $t.repairRunning : `${$t.repairSelected} (${repairList.filter(r => r.checked && !r.repaired).length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL GESTION DES DOUBLONS
          ═══════════════════════════════════════════════════════════ */}
      {dupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !dupRunning && setDupModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-600 to-rose-600 px-6 py-4 rounded-t-2xl flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white"><Copy className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{$t.dupTitle}</h3>
                  <p className="text-red-100 text-xs">{lang === 'fr' ? 'Détection par nom + date de naissance' : 'Detection by name + birth date'}</p>
                </div>
              </div>
              <button onClick={() => !dupRunning && setDupModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <p className="text-sm text-gray-600 bg-red-50 border border-red-100 rounded-lg p-3">{$t.dupDesc}</p>

              {/* Légende des 2 contrôles */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs bg-white border border-gray-200 rounded-lg px-3 py-2">
                <span className="flex items-center gap-1.5 font-medium text-gray-700">
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-green-600 bg-green-100 inline-block" />
                  {lang === 'fr' ? '◉ RADIO verte = fiche à GARDER (une seule par groupe)' : '◉ green radio = record to KEEP (one per group)'}
                </span>
                <span className="flex items-center gap-1.5 font-medium text-gray-700">
                  <span className="w-3.5 h-3.5 rounded border-2 border-red-600 bg-red-100 inline-block" />
                  {lang === 'fr' ? '☑ CASE rouge = fiche à SUPPRIMER' : '☑ red checkbox = record to DELETE'}
                </span>
                <span className="text-gray-400 italic">
                  {lang === 'fr' ? '→ Gardez la radio sur le matricule récent, la case de l\'ancien (2226-XXX) est déjà cochée : cliquez juste « Supprimer la sélection ».' : '→ Keep the radio on the recent matricule; the old one (2226-XXX) is already checked: just click "Delete selection".'}
                </span>
              </div>

              {/* V15 : portée + critère de détection */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600">{lang === 'fr' ? 'Portée :' : 'Scope:'}</span>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setDupScope('school')}
                      className={cn('px-3 py-1.5 text-xs font-medium transition-colors', dupScope === 'school' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
                    >
                      {lang === 'fr' ? "Toute l'école" : 'Whole school'}
                    </button>
                    <button
                      onClick={() => setDupScope('displayed')}
                      disabled={!filters.class_id && !filters.level_id && !filters.section_id && !filters.search?.trim()}
                      className={cn('px-3 py-1.5 text-xs font-medium transition-colors',
                        dupScope === 'displayed' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
                        (!filters.class_id && !filters.level_id && !filters.section_id && !filters.search?.trim()) && 'opacity-40 cursor-not-allowed')}
                      title={lang === 'fr' ? "Filtrez d'abord par classe/niveau/recherche sur la page des élèves" : 'Filter by class/level/search on the students page first'}
                    >
                      {lang === 'fr' ? 'Liste affichée (filtres)' : 'Displayed (filtered)'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600">{lang === 'fr' ? 'Critère :' : 'Criteria:'}</span>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setDupMode('strict')}
                      className={cn('px-3 py-1.5 text-xs font-medium transition-colors', dupMode === 'strict' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
                    >
                      {lang === 'fr' ? 'Nom + date naissance' : 'Name + birth date'}
                    </button>
                    <button
                      onClick={() => setDupMode('name')}
                      className={cn('px-3 py-1.5 text-xs font-medium transition-colors', dupMode === 'name' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
                      title={lang === 'fr' ? 'Détecte les homonymes même avec des dates de naissance différentes' : 'Detects namesakes even with different birth dates'}
                    >
                      {lang === 'fr' ? 'Nom seul' : 'Name only'}
                    </button>
                  </div>
                </div>
                <button
                  onClick={scanDuplicatesByName}
                  disabled={dupScanning || dupRunning}
                  className="ml-auto text-xs px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-all disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Loader2 className={cn('w-3.5 h-3.5', dupScanning ? 'animate-spin' : 'hidden')} />
                  {lang === 'fr' ? "Relancer l'analyse" : 'Re-run scan'}
                </button>
              </div>

              {/* État : scan */}
              {dupScanning && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="w-10 h-10 text-red-600 animate-spin" />
                  <p className="text-sm text-gray-500">{$t.dupScanning}</p>
                </div>
              )}

              {/* État : aucun doublon */}
              {!dupScanning && dupScanned && dupGroups.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{$t.dupNone}</p>
                </div>
              )}

              {/* État : doublons trouvés */}
              {!dupScanning && dupGroups.length > 0 && (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm font-semibold text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {dupGroups.length} {$t.dupGroups} · {dupSelectedCount} {$t.dupSelected}
                    </p>
                    <button
                      onClick={autoSelectOldMatricules}
                      disabled={dupRunning}
                      className="text-xs px-3 py-1.5 bg-white border border-red-200 text-red-700 rounded-lg font-medium hover:bg-red-50 transition-all disabled:opacity-50"
                    >
                      {lang === 'fr' ? 'Sélect. anciens matricules (2226-XXX)' : 'Select old matricules (2226-XXX)'}
                    </button>
                  </div>

                  {/* Barre de progression */}
                  {dupRunning && (
                    <div className="space-y-1">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-red-500 to-rose-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: dupProgress.total ? `${Math.round((dupProgress.done / dupProgress.total) * 100)}%` : '0%' }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-center">
                        {dupProgress.done} / {dupProgress.total}
                        {dupProgress.failed > 0 && <span className="text-red-600 font-medium"> — {dupProgress.failed} échec(s)</span>}
                      </p>
                    </div>
                  )}

                  {/* Liste des groupes */}
                  <div className="space-y-4">
                    {dupGroups.map((g) => (
                      <div key={g.key} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                          <div>
                            <p className="text-sm font-bold text-gray-900">{g.fullName}</p>
                            <p className="text-xs text-gray-500">
                              {dupMode === 'strict'
                                ? `${formatDate(g.dateOfBirth || undefined)} · ${g.members.length} ${lang === 'fr' ? 'enregistrements' : 'records'}`
                                : `${g.members.length} ${lang === 'fr' ? 'enregistrements' : 'records'} · ${new Set(g.members.map((m: any) => m.student.date_of_birth)).size > 1
                                    ? (lang === 'fr' ? '⚠ dates de naissance DIFFÉRENTES — vérifiez avant de supprimer' : '⚠ DIFFERENT birth dates — verify before deleting')
                                    : (lang === 'fr' ? 'même date de naissance' : 'same birth date')}`}
                            </p>
                          </div>
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
                            {g.members.length - 1} {lang === 'fr' ? 'doublon(s)' : 'duplicate(s)'}
                          </span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {g.members.map((m: any) => {
                            const s: Student = m.student
                            const isKeeper = s.id === g.keepId
                            const isOld = OLD_MATRICULE_RE.test(s.matricule || '')
                            return (
                              <div key={s.id} className={cn(
                                'flex items-center gap-3 px-4 py-2.5 border-l-4',
                                isKeeper
                                  ? 'bg-green-50/70 border-l-green-500'
                                  : m.checked
                                    ? 'bg-red-50/70 border-l-red-500'
                                    : 'border-l-transparent hover:bg-gray-50'
                              )}>
                                {/* ◉ RADIO verte : fiche à GARDER */}
                                <label className="flex items-center gap-1.5 shrink-0 cursor-pointer" title={$t.dupKeep}>
                                  <input
                                    type="radio"
                                    name={`keeper-${g.key}`}
                                    checked={isKeeper}
                                    onChange={() => setDupKeeper(g.key, s.id)}
                                    disabled={dupRunning}
                                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                  />
                                  <span className="text-[10px] font-bold text-green-700 uppercase">{$t.dupKeep}</span>
                                </label>
                                {/* ☑ CASE rouge : fiche à SUPPRIMER */}
                                <label className="flex items-center gap-1 shrink-0 cursor-pointer" title={lang === 'fr' ? 'Cocher pour supprimer cette fiche' : 'Check to delete this record'}>
                                  <input
                                    type="checkbox"
                                    checked={m.checked && !isKeeper}
                                    disabled={isKeeper || dupRunning}
                                    onChange={() => toggleDupMember(g.key, s.id)}
                                    className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                  />
                                  {m.checked && !isKeeper && (
                                    <span className="text-[10px] font-bold text-red-700 uppercase">
                                      {lang === 'fr' ? 'Suppr.' : 'Del.'}
                                    </span>
                                  )}
                                </label>
                                <span className={cn('font-mono text-xs px-2 py-0.5 rounded shrink-0', isOld ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700')}>
                                  {s.matricule}
                                  {isOld && <span className="ml-1 text-[9px] font-bold uppercase">{$t.dupOldBadge}</span>}
                                </span>
                                <span className="text-sm text-gray-800 flex-1 truncate">{getClassDisplayName(s)}</span>
                                <span className="hidden sm:block">{getStatusBadge(s.status)}</span>
                                <span className="text-xs text-gray-500 hidden md:block" title={lang === 'fr' ? 'Date de naissance' : 'Birth date'}>
                                  🎂 {formatDate(s.date_of_birth)}
                                </span>
                                <span className="text-xs text-gray-400 hidden lg:block">{formatDate(s.created_at)}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end gap-3 shrink-0 flex-wrap">
              <button
                onClick={() => setDupModalOpen(false)}
                disabled={dupRunning}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {$t.repairClose}
              </button>
              <button
                onClick={() => runDupAction('deactivate')}
                disabled={dupRunning || dupScanning || dupSelectedCount === 0}
                className="px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-50 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                {$t.dupDeactivateSel} ({dupSelectedCount})
              </button>
              <button
                onClick={() => runDupAction('delete')}
                disabled={dupRunning || dupScanning || dupSelectedCount === 0}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg text-sm font-medium hover:from-red-700 hover:to-rose-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {dupRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {$t.dupDeleteSel} ({dupSelectedCount})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL DE CONFIRMATION SUPPRESSION DÉFINITIVE (V11)
          ═══════════════════════════════════════════════════════════ */}
      {dupConfirmModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !dupRunning && setDupConfirmModal({ open: false, targets: [] })}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* En-tête rouge */}
            <div className="bg-gradient-to-r from-red-600 to-rose-700 px-6 py-4 rounded-t-2xl flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white"><Trash2 className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{$t.dupConfirmTitle}</h3>
                  <p className="text-red-100 text-xs">{dupConfirmModal.targets.length} {$t.dupConfirmCount}</p>
                </div>
              </div>
              <button onClick={() => !dupRunning && setDupConfirmModal({ open: false, targets: [] })} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {/* Avertissement */}
              <div className="flex items-start gap-3 bg-red-50 border-2 border-red-300 rounded-xl p-4">
                <AlertCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-red-800 uppercase tracking-wide mb-1">⚠ IRRÉVERSIBLE</p>
                  <p className="text-sm text-red-700">{$t.dupConfirmWarn}</p>
                </div>
              </div>

              {/* Liste détaillée des fiches concernées */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">{$t.dupConfirmList} :</p>
                <div className="border border-red-200 rounded-xl overflow-hidden">
                  <div className="max-h-52 overflow-y-auto divide-y divide-red-100">
                    {dupConfirmModal.targets.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 px-3 py-2 bg-red-50/40">
                        <Trash2 className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <span className={cn(
                          'font-mono text-xs px-2 py-0.5 rounded shrink-0',
                          OLD_MATRICULE_RE.test(s.matricule || '') ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'
                        )}>
                          {s.matricule}
                        </span>
                        <span className="text-sm font-semibold text-gray-900 flex-1 truncate">
                          {s.last_name} {s.first_name}
                        </span>
                        <span className="text-xs text-gray-500 hidden sm:block">{getClassDisplayName(s)}</span>
                        <span className="hidden sm:block">{getStatusBadge(s.status)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Note rassurante */}
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                <p className="text-xs text-green-700">{$t.dupConfirmNote}</p>
              </div>
            </div>

            {/* Pied */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end gap-3 shrink-0">
              <button
                onClick={() => setDupConfirmModal({ open: false, targets: [] })}
                disabled={dupRunning}
                className="px-5 py-2 bg-white border-2 border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {$t.cancel}
              </button>
              <button
                onClick={() => executeDupAction('delete', dupConfirmModal.targets)}
                disabled={dupRunning}
                className="px-5 py-2 bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-lg text-sm font-bold hover:from-red-700 hover:to-rose-800 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-red-200"
              >
                {dupRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {$t.dupConfirmBtn} ({dupConfirmModal.targets.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL MISE À JOUR EN MASSE DES CONTACTS
          ═══════════════════════════════════════════════════════════ */}
      {contactsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setContactsModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white"><Phone className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{$t.bulkContactsTitle}</h3>
                  <p className="text-blue-100 text-sm">{lang === 'fr' ? 'Mise à jour par fichier CSV' : 'Update via CSV file'}</p>
                </div>
              </div>
              <button onClick={() => setContactsModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">{$t.contactsTemplateHelp}</p>

              {/* Sélecteur de classe */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{$t.classFilter}</label>
                <select value={contactsClassId} onChange={(e) => setContactsClassId(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="">{lang === 'fr' ? 'Toutes les classes' : 'All classes'}</option>
                  {classes.map((c: ClassItem) => (<option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>))}
                </select>
              </div>

              {/* Étape 1 : Télécharger le modèle */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><FileDown className="w-4 h-4 text-blue-600" />{$t.downloadTemplate}</h4>
                <p className="text-xs text-gray-500 mb-3">{lang === 'fr' ? 'Téléchargez le modèle pré-rempli avec les élèves de la classe sélectionnée.' : 'Download the pre-filled template with students from the selected class.'}</p>
                <button onClick={handleDownloadContactsTemplate} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 transition-all">
                  <FileText className="w-4 h-4" />{$t.downloadTemplate}
                </button>
              </div>

              {/* Étape 2 : Uploader le fichier rempli */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-600" />{$t.uploadContactsFile}</h4>
                <p className="text-xs text-gray-500 mb-3">{lang === 'fr' ? 'Sélectionnez le fichier CSV rempli avec les nouveaux contacts.' : 'Select the filled CSV file with the new contacts.'}</p>
                <label className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 border-dashed rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer transition-all">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  {contactsFile ? contactsFile.name : (lang === 'fr' ? 'Choisir un fichier CSV...' : 'Choose a CSV file...')}
                  <input type="file" accept=".csv" onChange={handleContactsFileChange} className="hidden" />
                </label>
                {contactsFile && (
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{contactsFile.name} — {(contactsFile.size / 1024).toFixed(1)} KB</p>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => { setContactsModalOpen(false); setContactsFile(null); }} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">{$t.cancel}</button>
              <button onClick={handleImportContacts} disabled={contactsLoading || !contactsFile} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center gap-2">
                {contactsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {lang === 'fr' ? 'Mettre à jour' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentsPage